import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard, CheckCircle2, Clock, XCircle, FileText, Eye, Download,
  Search, Filter, Plus, ChevronDown, AlertCircle, Banknote, RefreshCw,
  UserCheck, Loader2, X, Paperclip, Upload, Trash2, LinkIcon, Pencil, History,
  DollarSign, Building2, CalendarDays, ShieldAlert, ListTodo, Sparkles, AlertTriangle, ShieldCheck
} from "lucide-react";
import TareasPanel from "../components/TareasPanel";   // Fix DO (2026-09-22)
import SelectBuscable from "../components/SelectBuscable";   // Fix EE (2026-09-24)
import { opcionesObras } from "../utils/obras";   // Fix HG (2026-10-20)
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { useAuth } from "../contexts/AuthContext";
import { formatPYG, formatPYGCompact, formatDate, todayAsuncion } from "../utils/format";
import { roleAllowed } from "../utils/roles";
import { invoiceNumbersLabel } from "../utils/invoices";
import { Skeleton } from "../components/ui/skeleton";
import EstadoCuentaModal from "../components/EstadoCuentaModal";   // Fix FO (2026-10-09)
import { VinculacionCerradaInfo } from "./compras/VinculacionCerrada";   // Fix GS (2026-10-17)
import PerfilesAprobacionPagos, { ChipsAprobacionOP, textoTramoOP } from "./TesoreriaPerfilesAprobacion";   // Fix GT (2026-10-17)
import { toast } from "sonner";
import { estadoDocumentalPago } from "../utils/conciliacionPago";

// ── Helpers ────────────────────────────────────────────────────────────────────
const TABS = [
  // Fix DO (2026-09-22): "agregar a TESORERIA y FINANZAS su sección de TAREAS, a modo
  // que cada cuál en su módulo solo vean tareas pertenecientes a dicho módulo" (CEN, 21/09).
  { key: "tareas", label: "Tareas", icon: ListTodo },
  { key: "contado", label: "Contado", icon: Banknote },
  { key: "credito", label: "A Crédito", icon: CreditCard },
  { key: "pagodirecto", label: "Pagos Serv. Tercerizados", icon: UserCheck },
  { key: "ordenes", label: "Órdenes de Pago", icon: FileText },
  { key: "cxp", label: "Cuentas x Pagar", icon: AlertCircle },
  // Fix GT (2026-10-17): "En pagos podemos agregar también 'perfiles de aprobación' al final" (CEN).
  { key: "perfiles", label: "Perfiles de aprobación", icon: ShieldCheck },
];

const STATUS_MAP = {
  pendiente_aprobacion: { label: "Pendiente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  aprobada: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rechazada: { label: "Rechazada", cls: "bg-red-100 text-red-700 border-red-200" },
  pagada: { label: "Pagada", cls: "bg-blue-100 text-blue-700 border-blue-200" },
};

const TYPE_BADGE = {
  OC: "bg-blue-100 text-blue-700",
  OTS: "bg-purple-100 text-purple-700",
  "OTS-ALQ": "bg-orange-100 text-orange-700",
  DIRECTO: "bg-indigo-100 text-indigo-700",
};

// Fix GQ (2026-10-17): facturas vinculadas en Compras a la OC / certificación de cada ítem de la OP
// (el backend las lee del origen cada vez que se muestra la OP).
const facturasDeCompras = (op) => {
  const vistas = new Set();
  return (op?.items || []).flatMap(it => it.facturas_compras || []).filter(f => {
    const k = `${f.source_id}|${f.invoice_id}|${f.invoice_number}`;
    if (vistas.has(k)) return false;
    vistas.add(k);
    return true;
  });
};

// Fix GR (2026-10-17): "que las cargas de Comprobantes sean validadas por IA también cuando se cargan"
// (CEN). Al adjuntar el comprobante de pago o la factura, la IA lo lee y el servidor lo cruza con la OP;
// con alertas, se guarda recién cuando quien carga las confirma. Si la IA no responde, se carga a mano.
const MAX_COMPROBANTES = 6;

function useLecturaIAPago(opId, { aplicar } = {}) {
  const [leyendo, setLeyendo] = useState(false);
  const [res, setRes] = useState(null);            // { lectura, alertas, autocompletar } o { error }
  const [alertas, setAlertas] = useState([]);
  const [confirmadas, setConfirmadas] = useState(false);
  const [docType, setDocType] = useState("");
  const turno = useRef(0);
  const aplicarRef = useRef(aplicar);
  aplicarRef.current = aplicar;
  const leer = useCallback(async (dataUrl, tipo, monto, porTransferencia = false) => {
    const n = ++turno.current;
    setRes(null); setAlertas([]); setConfirmadas(false); setDocType(tipo || "");
    if (!dataUrl || !["comprobante_pago", "factura"].includes(tipo)) { setLeyendo(false); return; }
    setLeyendo(true);
    try {
      const { data } = await axios.post(`${API}/treasury/payment-orders/${opId}/leer-comprobante`,
        { comprobante_base64: dataUrl, doc_type: tipo, monto: monto || null, por_transferencia: porTransferencia }, { withCredentials: true });
      if (n !== turno.current) return;
      setRes(data); setAlertas(data.alertas || []);
      aplicarRef.current?.(data.autocompletar || {});
    } catch (err) {
      if (n !== turno.current) return;
      const det = err.response?.data?.detail;
      setRes({ error: typeof det === "string" ? det : "No se pudo leer el documento con IA: completá los datos a mano" });
    } finally {
      if (n === turno.current) setLeyendo(false);
    }
  }, [opId]);
  return {
    leyendo, res, alertas, confirmadas, setConfirmadas, docType, leer,
    payload: () => ({ lectura_ia: res?.lectura || null, alertas_confirmadas: confirmadas ? alertas : [] }),
    bloquea: leyendo || (alertas.length > 0 && !confirmadas),
    // 409 del servidor al guardar: las alertas con lo cargado (hay que volver a confirmarlas)
    errorAlConfirmar: (err) => {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det && Array.isArray(det.alertas)) {
        setAlertas(det.alertas); setConfirmadas(false);
        toast.error(det.mensaje || "Revisá las alertas del documento");
        return true;
      }
      return false;
    },
  };
}

function PanelLecturaIAPago({ ia }) {
  if (!ia.leyendo && !ia.res && ia.alertas.length === 0) return null;
  const l = ia.res?.lectura;
  const factura = ia.docType === "factura";
  const monto = (v) => (l?.moneda === "USD" ? `US$ ${Number(v || 0).toLocaleString("es-PY")}` : formatPYG(v || 0));
  return (
    <div className="space-y-2" data-testid="op-ia-panel">
      {ia.leyendo && (
        <p className="flex items-center gap-1.5 text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2" data-testid="op-ia-leyendo">
          <Loader2 size={13} className="animate-spin" /> Leyendo {factura ? "la factura" : "el comprobante"} con IA…
        </p>
      )}
      {ia.res?.error && (
        <p className="text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2" data-testid="op-ia-error">
          {ia.res.error}. Queda «sin lectura IA».
        </p>
      )}
      {l && (
        <div className="text-xs bg-white border border-sky-100 rounded-lg px-3 py-2 text-gray-700" data-testid="op-ia-leido">
          <p className="flex items-center gap-1 font-semibold text-sky-800 mb-0.5"><Sparkles size={12} /> Leído por IA (confianza {l.confianza} %)</p>
          {factura ? (
            <p>Factura {l.numero || "—"} · {l.total ? monto(l.total) : "Total sin leer"} · {l.fecha ? formatDate(l.fecha) : "sin fecha"}
              {l.emisor ? ` · ${l.emisor}` : ""}{l.timbrado ? ` · Timbrado ${l.timbrado}` : ""}</p>
          ) : (
            <p>{l.monto ? monto(l.monto) : "Monto sin leer"} · N° {l.numero || "—"} · {l.fecha ? formatDate(l.fecha) : "sin fecha"}
              {l.banco ? ` · ${l.banco}` : ""}{l.beneficiario ? ` · Para: ${l.beneficiario}` : ""}</p>
          )}
        </div>
      )}
      {l && ia.alertas.length === 0 && (
        <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="op-ia-ok">
          <CheckCircle2 size={13} /> {factura ? "La factura coincide" : "El comprobante coincide"} con la Orden de Pago
        </p>
      )}
      {ia.alertas.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1" data-testid="op-ia-alertas">
          <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={13} /> Revisá antes de guardar</p>
          <ul className="list-disc pl-4 space-y-0.5">{ia.alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>
          <label className="flex items-start gap-2 pt-1 cursor-pointer font-semibold">
            <input type="checkbox" checked={ia.confirmadas} onChange={e => ia.setConfirmadas(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="op-ia-confirmar" />
            Revisé estas alertas y confirmo igual
          </label>
        </div>
      )}
    </div>
  );
}

// Fix GR: resultado del control con IA guardado en la factura o el comprobante de la OP.
function ControlIABadgePago({ c }) {
  if (!c) return null;
  const est = {
    verificado: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Verificado con IA"],
    con_alertas: ["bg-amber-50 text-amber-700 border-amber-200", "Con alertas confirmadas"],
    sin_lectura: ["bg-gray-50 text-gray-500 border-gray-200", "Sin lectura IA"],
  }[c.estado] || ["bg-gray-50 text-gray-500 border-gray-200", c.estado];
  return <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${est[0]}`} data-testid="op-control-ia">{est[1]}</span>;
}

function StatCard({ title, value, sub, color = "gray", icon: Icon }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    red: "bg-red-50 text-red-600 border-red-100",
    gray: "bg-gray-50 text-gray-600 border-gray-100",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className={`p-2 rounded-lg border ${colors[color]}`}><Icon size={15} /></div>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── PDF Download (Auditoría 2026-08-27, CAMBIO 4 — ahora se genera en el
// backend con reportlab, leyendo los datos de empresa desde Configuración) ──
async function downloadOPPdf(op) {
  try {
    const res = await axios.get(`${API}/treasury/payment-orders/${op._id}/export-pdf`, {
      withCredentials: true, responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${op.number}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error("Error al generar el PDF de la orden de pago");
  }
}

// ── Linked Doc Detail Modal (BLOQUE 32 — click en OC/AP/PP/PM abre su detalle) ──
function LinkedDocDetailModal({ doc, onClose }) {
  if (!doc) return null;
  const d = doc.detail || {};
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="linked-doc-detail-modal">
        <div className="bg-slate-800 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">{doc.tipo}</p>
            <p className="text-lg font-bold">{d.number || doc.code}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="close-linked-doc-detail">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          {doc.tipo === "OC" && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-gray-400 uppercase">Proveedor</p><p className="font-semibold">{d.provider_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Condición de Pago</p><p className="font-semibold capitalize">{d.condicion_pago || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Proyecto</p><p className="font-semibold">{d.project_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Estado</p><p className="font-semibold capitalize">{d.status || "—"}</p></div>
              </div>
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-slate-100"><tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Descripción</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Cant.</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Subtotal</th>
                </tr></thead>
                <tbody>
                  {(d.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-2">{it.item_name || it.description || it.name || "—"}</td>
                      <td className="px-3 py-2 text-right">{it.quantity || 0} {it.unit || ""}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatPYG(it.subtotal || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right text-sm font-bold text-slate-800">TOTAL: {formatPYG(d.total || 0)}</div>
              {/* BLOQUE 51 PARTE B — saldo pendiente de facturar, visible aunque la OC
                  todavía no tenga ninguna factura vinculada (0% facturado). */}
              {(() => {
                const invoicedTotal = (d.invoices || []).reduce((sum, inv) => sum + Number(inv.invoice_total || 0), 0);
                const pendingTotal = Math.max((d.total || 0) - invoicedTotal, 0);
                return pendingTotal > 0 && !d.vinculacion_facturas_cerrada ? (
                  <div className="px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-[11px] font-bold text-amber-800" data-testid="op-falta-facturar">
                    Facturado {formatPYG(invoicedTotal)} · Falta facturar {formatPYG(pendingTotal)}
                  </div>
                ) : null;
              })()}
              {/* Fix GS (2026-10-17): la vinculación cerrada con diferencia, con su motivo. */}
              <VinculacionCerradaInfo doc={d} cov={d.invoice_coverage_summary} testId="op-oc-vinc-cerrada" />
              {/* BLOQUE 49 — facturas vinculadas con acceso a su documento real */}
              {(d.invoices || []).length > 0 && (
                <div className="rounded-xl border border-emerald-200 overflow-hidden" data-testid="op-linked-invoices">
                  <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200">
                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Facturas Vinculadas ({d.invoices.length})</p>
                  </div>
                  <div className="divide-y divide-emerald-100">
                    {d.invoices.map((inv, i) => (
                      <div key={inv.invoice_id || i} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`op-invoice-row-${i}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 font-mono">{inv.invoice_number || "—"}</p>
                          <p className="text-[11px] text-gray-500">{formatPYG(inv.invoice_total || 0)}</p>
                        </div>
                        {inv.doc_available ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => window.open(`${API}/purchases/oc-from-pf/${d.oc_id}/invoices/${inv.invoice_id}/document?inline=1`, "_blank")}
                              className="flex items-center gap-1 text-[11px] text-[#2E6DA4] border border-[#2E6DA4]/30 hover:bg-[#2E6DA4]/10 rounded-lg px-2 py-1"
                              data-testid={`op-view-invoice-doc-${i}`}>
                              <Eye size={11} /> Ver
                            </button>
                            <button
                              onClick={() => window.open(`${API}/purchases/oc-from-pf/${d.oc_id}/invoices/${inv.invoice_id}/document`, "_blank")}
                              className="flex items-center gap-1 text-[11px] text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg px-2 py-1"
                              data-testid={`op-download-invoice-doc-${i}`}>
                              <Download size={11} /> Descargar
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400 italic flex-shrink-0">Sin documento</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {doc.tipo === "AP" && (
            <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-slate-100"><tr>
                <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Ítem</th>
                <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Proveedor(es) Adjudicado(s)</th>
                <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Cant. / P.U.</th>
              </tr></thead>
              <tbody>
                {(d.items_view || []).map((it, i) => (
                  <tr key={i} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2">{it.description}</td>
                    <td className="px-3 py-2">
                      {it.providers.length === 0 ? <span className="text-gray-400">Sin selec.</span> : it.providers.map((p, j) => (
                        <div key={j} className="text-xs">{p.provider}</div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {it.providers.map((p, j) => (
                        <div key={j} className="text-xs">{p.quantity} × {formatPYG(p.unit_price)}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(doc.tipo === "PP" || doc.tipo === "PM") && (
            <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-slate-100"><tr>
                <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Descripción</th>
                <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Cant.</th>
              </tr></thead>
              <tbody>
                {(d.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="px-3 py-2">{it.material_name || it.item_name || it.description || it.name || "—"}</td>
                    <td className="px-3 py-2 text-right">{it.quantity || 0} {it.unit || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Fix GH (2026-10-15): "en la parte de pagos, cuando es servicio, sería buena también poder
              visualizar la OTS y el Cert, como lo hacemos con las OPs de materiales" (CEN). */}
          {doc.tipo === "OTS" && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="linked-doc-ots-detalle">
                <div><p className="text-xs text-gray-400 uppercase">Proveedor</p><p className="font-semibold">{d.provider_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Condición de Pago</p><p className="font-semibold capitalize">{d.condicion_pago || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Proyecto</p><p className="font-semibold">{d.project_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Estado</p><p className="font-semibold capitalize">{d.status || "—"}</p></div>
              </div>
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-slate-100"><tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Descripción</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Cant.</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Subtotal</th>
                </tr></thead>
                <tbody>
                  {(d.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-2">{it.description || "—"}</td>
                      <td className="px-3 py-2 text-right">{it.quantity || 0} {it.unit || ""}{it.dias ? ` · ${it.dias} días` : ""}</td>
                      <td className="px-3 py-2 text-right font-semibold">{formatPYG(it.subtotal || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right text-sm font-bold text-slate-800">TOTAL: {formatPYG(d.total || 0)}</div>
              {d.notes && <p className="text-xs text-gray-500"><span className="font-semibold">Notas:</span> {d.notes}</p>}
            </>
          )}
          {doc.tipo === "CRT" && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="linked-doc-crt-detalle">
                <div><p className="text-xs text-gray-400 uppercase">Orden de Trabajo</p><p className="font-semibold">{d.ots_number || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Proveedor</p><p className="font-semibold">{d.provider_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Proyecto</p><p className="font-semibold">{d.project_name || "—"}</p></div>
                <div><p className="text-xs text-gray-400 uppercase">Tipo</p><p className="font-semibold">{d.anticipo ? "Anticipo" : d.categoria === "alquiler" ? "Certificación de alquiler" : "Certificado de servicio"}</p></div>
                {(d.period_from || d.period_to) && (
                  <div><p className="text-xs text-gray-400 uppercase">Período</p><p className="font-semibold">{d.period_from || "—"} al {d.period_to || "—"}</p></div>
                )}
                {d.invoice_number && (
                  <div><p className="text-xs text-gray-400 uppercase">Factura</p><p className="font-semibold font-mono">{d.invoice_number}</p></div>
                )}
              </div>
              {(d.certified_items || []).length > 0 && (
                <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                  <thead className="bg-slate-100"><tr>
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500">Descripción</th>
                    <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Cant.</th>
                    <th className="text-right px-3 py-2 text-xs font-bold text-gray-500">Subtotal</th>
                  </tr></thead>
                  <tbody>
                    {d.certified_items.map((it, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-2">{it.description || "—"}</td>
                        <td className="px-3 py-2 text-right">{it.quantity || 0} {it.unit || ""}{it.dias ? ` · ${it.dias} días` : ""}</td>
                        <td className="px-3 py-2 text-right font-semibold">{formatPYG(it.subtotal || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="text-right text-sm space-y-0.5">
                {Number(d.anticipo_descontado) > 0 && (
                  <>
                    <p className="text-gray-600">Monto del período: {formatPYG(d.monto_periodo || 0)}</p>
                    <p className="text-amber-700">Anticipo descontado: − {formatPYG(d.anticipo_descontado)}</p>
                  </>
                )}
                <p className="font-bold text-slate-800" data-testid="linked-doc-crt-monto">MONTO CERTIFICADO: {formatPYG(d.certified_amount || 0)}</p>
                {Number(d.iva_amount) > 0 && <p className="text-xs text-gray-400">IVA incluido: {formatPYG(d.iva_amount)}</p>}
              </div>
              <VinculacionCerradaInfo doc={d} testId="op-crt-vinc-cerrada" />{/* Fix GS */}
              {d.notes && <p className="text-xs text-gray-500"><span className="font-semibold">Notas:</span> {d.notes}</p>}
            </>
          )}
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Gestión de adjuntos de la OP: editar datos, reemplazar archivo, quitar ─────
const OP_DOC_GESTION_ROLES = ["Superadministrador", "Administrador", "Gerencia Financiera"];
const errTexto = (err, base) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : (d?.mensaje || base);
};

function DocEditarForm({ op, doc, idx, onOp, onClose }) {
  const esComp = doc.doc_type === "comprobante_pago" && doc.monto_transferencia != null;
  const [f, setF] = useState({ doc_name: doc.doc_name || "", notes: doc.notes || "", monto: doc.monto_manual?.valor ?? doc.monto_transferencia ?? "",
    fecha: doc.fecha_transferencia || "", ref: doc.referencia_transferencia || "" });
  const [montoTocado, setMontoTocado] = useState(false);   // sólo una edición expresa del importe lo corrige
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { if (k === "monto") setMontoTocado(true); setF(v => ({ ...v, [k]: e.target.value })); };
  const montoIA = doc.control_ia?.leido?.monto;
  const guardar = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { doc_id: doc.id || null, doc_name: f.doc_name, notes: f.notes };
      if (esComp) Object.assign(body, { fecha_transferencia: f.fecha, referencia_transferencia: f.ref, ...(montoTocado ? { monto_transferencia: Number(f.monto) } : {}) });
      const { data } = await axios.put(`${API}/treasury/payment-orders/${op._id}/documents/${idx}`, body, { withCredentials: true });
      onOp(data); toast.success("Documento actualizado"); onClose();
    } catch (err) { toast.error(errTexto(err, "No se pudo guardar; reintentá")); }
    finally { setBusy(false); }
  };
  const inp = "w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white";
  return (
    <div className="px-4 py-3 bg-blue-50/40 border-t border-blue-100 space-y-2" data-testid={`op-doc-editar-form-${idx}`}>
      <p className="text-xs font-semibold text-gray-700">Editar datos del documento (no cambia el archivo ni el pago)</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="col-span-2">Nombre<input value={f.doc_name} onChange={set("doc_name")} className={inp} data-testid="op-doc-editar-nombre" /></label>
        <label className="col-span-2">Nota<input value={f.notes} onChange={set("notes")} className={inp} data-testid="op-doc-editar-nota" /></label>
        {esComp && <>
          <label>Importe (Gs.)<input type="number" min="1" value={f.monto} onChange={set("monto")} className={inp} data-testid="op-doc-editar-monto" /></label>
          <label>Fecha<input type="date" value={f.fecha} onChange={set("fecha")} className={inp} /></label>
          <label className="col-span-2">Referencia<input value={f.ref} onChange={set("ref")} className={inp} /></label>
          {montoTocado && <p className="col-span-2 text-[11px] text-amber-800" data-testid="op-doc-editar-aviso-monto">
            Este importe pasa a ser el que usa la conciliación documental{montoIA ? ` (la IA leyó ${formatPYG(montoIA)}; la lectura se conserva)` : ""}. No modifica el pago registrado.</p>}
        </>}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200">Cancelar</button>
        <button type="button" onClick={guardar} disabled={busy || !f.doc_name.trim()} data-testid="op-doc-editar-guardar"
          className="flex items-center gap-1 bg-[#2E6DA4] text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
          {busy && <Loader2 size={11} className="animate-spin" />} Guardar
        </button>
      </div>
    </div>
  );
}

function DocQuitarConfirm({ op, doc, idx, onOp, onClose }) {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const quitar = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/treasury/payment-orders/${op._id}/documents/${idx}`,
        { params: { doc_id: doc.id || undefined, motivo: motivo || undefined }, withCredentials: true });
      onOp(data.orden); toast.success("Documento quitado de la Orden de Pago"); onClose();
    } catch (err) { toast.error(errTexto(err, "No se pudo quitar; reintentá")); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 py-3 bg-red-50/50 border-t border-red-100 space-y-2" data-testid={`op-doc-quitar-confirm-${idx}`}>
      <p className="text-xs text-red-800">¿Quitar «{doc.doc_name}» de esta Orden de Pago? El archivo queda guardado en el historial; el pago, el importe y el estado de la OP no cambian.</p>
      <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo (opcional)" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="op-doc-quitar-motivo" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200">Cancelar</button>
        <button type="button" onClick={quitar} disabled={busy} data-testid="op-doc-quitar-confirmar"
          className="flex items-center gap-1 bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
          {busy && <Loader2 size={11} className="animate-spin" />} Quitar adjunto
        </button>
      </div>
    </div>
  );
}

function DocReemplazarForm({ op, doc, idx, onOp, onClose }) {
  const [f, setF] = useState({ doc_name: "", content_base64: "", monto: doc.monto_transferencia ?? "", fecha: doc.fecha_transferencia || "", ref: doc.referencia_transferencia || "" });
  const [busy, setBusy] = useState(false);
  const esComp = doc.doc_type === "comprobante_pago";
  const ia = useLecturaIAPago(op._id, { aplicar: a => setF(v => ({ ...v,
    fecha: a.fecha || v.fecha, ref: a.referencia || v.ref, monto: a.monto && !v.monto ? String(a.monto) : v.monto })) });
  const { leer } = ia;
  useEffect(() => { leer(f.content_base64, doc.doc_type); }, [leer, f.content_base64, doc.doc_type]);
  const elegir = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo no puede superar 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setF(v => ({ ...v, doc_name: file.name, content_base64: ev.target.result }));
    reader.readAsDataURL(file);
  };
  const reemplazar = async () => {
    if (busy || ia.bloquea || !f.content_base64) return;
    setBusy(true);
    try {
      const body = { doc_id: doc.id || null, doc_name: f.doc_name, content_base64: f.content_base64, ...ia.payload() };
      if (esComp) Object.assign(body, { monto_transferencia: f.monto ? Number(f.monto) : null, fecha_transferencia: f.fecha || null, referencia_transferencia: f.ref || null });
      const { data } = await axios.post(`${API}/treasury/payment-orders/${op._id}/documents/${idx}/reemplazar`, body, { withCredentials: true });
      onOp(data); toast.success("Archivo reemplazado; la versión anterior queda en el historial"); onClose();
    } catch (err) { if (!ia.errorAlConfirmar(err)) toast.error(errTexto(err, "No se pudo reemplazar; el adjunto actual se conserva")); }
    finally { setBusy(false); }
  };
  const inp = "w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white";
  return (
    <div className="px-4 py-3 bg-amber-50/40 border-t border-amber-100 space-y-2" data-testid={`op-doc-reemplazar-form-${idx}`}>
      <p className="text-xs font-semibold text-gray-700">Reemplazar archivo (la versión actual se conserva en el historial)</p>
      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={elegir} className="text-xs text-gray-600 w-full" data-testid="op-doc-reemplazar-archivo" />
      {esComp && (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <label>Importe (Gs.)<input type="number" min="1" value={f.monto} onChange={e => setF(v => ({ ...v, monto: e.target.value }))} className={inp} /></label>
          <label>Fecha<input type="date" value={f.fecha} onChange={e => setF(v => ({ ...v, fecha: e.target.value }))} className={inp} /></label>
          <label>Referencia<input value={f.ref} onChange={e => setF(v => ({ ...v, ref: e.target.value }))} className={inp} /></label>
        </div>
      )}
      <PanelLecturaIAPago ia={ia} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200">Cancelar</button>
        <button type="button" onClick={reemplazar} disabled={busy || !f.content_base64 || ia.bloquea} data-testid="op-doc-reemplazar-confirmar"
          className="flex items-center gap-1 bg-[#2E6DA4] text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">
          {busy && <Loader2 size={11} className="animate-spin" />} Reemplazar
        </button>
      </div>
    </div>
  );
}

function DocAccionPanel({ accion, ...props }) {
  if (accion === "editar") return <DocEditarForm {...props} />;
  if (accion === "reemplazar") return <DocReemplazarForm {...props} />;
  if (accion === "quitar") return <DocQuitarConfirm {...props} />;
  return null;
}

// ── View Modal ─────────────────────────────────────────────────────────────────
function OPDetailModal({ op: initialOp, onClose, onRefresh }) {
  const { user: usuario } = useAuth();
  const puedeGestionarDocs = roleAllowed(usuario?.role, OP_DOC_GESTION_ROLES);
  const [docAccion, setDocAccion] = useState(null);   // { idx, modo }
  const [verRetirados, setVerRetirados] = useState(false);
  const [op, setOp] = useState(initialOp);
  const [linkedDocs, setLinkedDocs] = useState(null);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [closingPayment, setClosingPayment] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doc_type: "factura", doc_name: "", notes: "", content_base64: "" });
  const [showUploadForm, setShowUploadForm] = useState(false);
  const fileInputRef = useRef(null);
  const [multi, setMulti] = useState([]);
  const [multiLect, setMultiLect] = useState({});
  const [multiAlertas, setMultiAlertas] = useState({});
  const [grupoAl, setGrupoAl] = useState([]);
  const [grupoOk, setGrupoOk] = useState(false);
  const [montoPagoAdj, setMontoPagoAdj] = useState("");
  // Fix GR (2026-10-17): la IA lee la factura o el comprobante de pago al elegir el archivo (y si cambia el tipo).
  const ia = useLecturaIAPago(initialOp?._id);
  const { leer: leerIA } = ia;
  useEffect(() => { leerIA(uploadForm.content_base64, uploadForm.doc_type); }, [leerIA, uploadForm.content_base64, uploadForm.doc_type]);

  useEffect(() => {
    // Load linked documents (system-linked AP, OC, CRT)
    const fetchLinked = async () => {
      if (!op?._id) return;
      setLoadingLinked(true);
      try {
        const res = await axios.get(`${API}/treasury/payment-orders/${op._id}/linked-docs`, { withCredentials: true });
        setLinkedDocs(res.data);
      } catch { /* non-critical */ }
      finally { setLoadingLinked(false); }
    };
    fetchLinked();
  }, [op?._id]);

  const handleFileSelect = async (e) => {
    const todos = Array.from(e.target.files || []);
    if (!todos.length) return;
    if (uploadForm.doc_type === "comprobante_pago" && todos.length > 1) {
      const files = recortarLote(todos, 0);
      if (files.some(f => f.size > 5 * 1024 * 1024)) { toast.error("Cada comprobante admite hasta 5 MB"); return; }
      try {
        setMulti(await leerArchivosLote(files));
        setMultiLect({}); setMultiAlertas({}); setGrupoAl([]); setGrupoOk(false);
        setUploadForm(f => ({ ...f, doc_name: "", content_base64: "" }));
      } catch (err) { toast.error(err.message); }
      return;
    }
    setMulti([]);
    const file = todos[0];
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo no puede superar 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setUploadForm(f => ({ ...f, doc_name: file.name, content_base64: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const multiLista = multi.map(a => multiLect[a.id]);
  const multiSuma = multiLista.reduce((s, d) => s + (Number(d?.monto) || 0), 0);
  const multiParcial = Number(montoPagoAdj) > 0 && Number(montoPagoAdj) < Number(op?.total || 0);
  const multiEsperado = multiParcial ? Number(montoPagoAdj) : Number(op?.total || 0);
  useEffect(() => { setGrupoAl([]); setGrupoOk(false); }, [multi.length, multiSuma, multiEsperado]);
  const multiBloquea = multi.length > 0 && (multiLista.some(d => !d || d.bloquea || !Number.isFinite(d.monto) || d.monto <= 0) || (grupoAl.length > 0 && !grupoOk));

  const handleUploadMulti = async () => {
    if (multiBloquea) { toast.error("Esperá la lectura IA y revisá importes y alertas de cada comprobante"); return; }
    setUploading(true);
    try {
      const res = await axios.post(`${API}/treasury/payment-orders/${op._id}/documents`, {
        doc_type: "comprobante_pago", doc_name: `${multi.length} comprobantes`, notes: uploadForm.notes,
        archivos: multiLista.map(({ bloquea: _, ...d }) => d), ...(multiParcial ? { monto_pago: Number(montoPagoAdj) } : {}),
        ...(grupoOk ? { alertas_grupo_confirmadas: grupoAl } : {}) }, { withCredentials: true });
      setOp(prev => ({ ...res.data, aprobacion_perfiles: prev?.aprobacion_perfiles }));
      toast.success(`${multi.length} comprobantes adjuntados`);
      setShowUploadForm(false); setMulti([]); setMontoPagoAdj("");
      setUploadForm({ doc_type: "factura", doc_name: "", notes: "", content_base64: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det?.grupo) { setGrupoAl(det.alertas || []); setGrupoOk(false); }
      if (err.response?.status === 409 && Number.isInteger(det?.comprobante) && multi[det.comprobante]) {
        setMultiAlertas(prev => ({ ...prev, [multi[det.comprobante].id]: det.alertas || [] }));
      }
      toast.error(typeof det === "string" ? det : [det?.mensaje, ...(det?.alertas || [])].filter(Boolean).join(" · ") || "Error al adjuntar");
    } finally { setUploading(false); }
  };

  const handleUpload = async () => {
    if (!uploadForm.doc_name) { toast.error("Seleccione un archivo"); return; }
    if (ia.bloquea) { toast.error(ia.leyendo ? "Esperá a que termine la lectura con IA" : "Revisá y confirmá las alertas del documento"); return; }   // Fix GR
    setUploading(true);
    try {
      const res = await axios.post(`${API}/treasury/payment-orders/${op._id}/documents`, { ...uploadForm, ...ia.payload() }, { withCredentials: true });
      setOp(prev => ({ ...res.data, aprobacion_perfiles: prev?.aprobacion_perfiles }));   // Fix GT: conserva quién aprueba
      toast.success("Documento adjuntado correctamente");
      setShowUploadForm(false);
      setUploadForm({ doc_type: "factura", doc_name: "", notes: "", content_base64: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) { if (!ia.errorAlConfirmar(err)) toast.error(err.response?.data?.detail || "Error al adjuntar"); }   // Fix GR
    finally { setUploading(false); }
  };

  const handleDocActualizado = (data) => {
    setOp(prev => ({ ...data, aprobacion_perfiles: prev?.aprobacion_perfiles }));
    if (onRefresh) onRefresh();
  };

  const handleClosePayment = async () => {
    if (!window.confirm("¿Confirmar cierre de pago? Esto marcará la OP como PAGADA.")) return;
    setClosingPayment(true);
    try {
      const res = await axios.put(`${API}/treasury/payment-orders/${op._id}/close-payment`, {}, { withCredentials: true });
      setOp(res.data);
      toast.success("Pago cerrado — OP marcada como Pagada");
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cerrar pago"); }
    finally { setClosingPayment(false); }
  };

  if (!op) return null;
  const st = estadoDocumentalPago(op) || STATUS_MAP[op.status] || { label: op.status, cls: "bg-gray-100 text-gray-600" };
  const isOPE = op.type === "directo";
  const todosDocs = (op.documents || []).map((doc, idx) => ({ doc, idx }));
  const uploadedDocs = todosDocs.filter(x => !x.doc.retirado);   // idx real: abre y gestiona el mismo documento
  const docsRetirados = todosDocs.filter(x => x.doc.retirado);
  const facturasCompras = facturasDeCompras(op);   // Fix GQ (2026-10-17)
  const hasFactura = uploadedDocs.some(x => x.doc.doc_type === "factura") || facturasCompras.length > 0;
  const hasComprobante = uploadedDocs.some(x => x.doc.doc_type === "comprobante_pago");
  const canClosePayment = op.status === "aprobada" && isOPE && hasFactura && hasComprobante;
  const missingDocsForClose = isOPE && op.status === "aprobada" ? [!hasFactura && "Factura", !hasComprobante && "Comprobante de Pago"].filter(Boolean) : [];

  const DOC_TYPE_LABELS = {
    factura: "Factura",
    comprobante_pago: "Comprobante de Pago",
    otro: "Otro Documento",
  };

  return (
    <>
      {createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-800 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide">Orden de Pago</p>
            <p className="text-lg font-bold">{op.number}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${st.cls}`}>{st.label}</span>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors ml-2">&times;</button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* Meta info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Tipo</p><p className="font-semibold">{op.type === "contado" ? "Contado (OP-M)" : op.type === "credito" ? "Crédito (OP-C)" : "Directo (OP-D)"}</p></div>
            <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Proyecto</p><p className="font-semibold">{op.project_name || "—"}</p></div>
            <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Creado por</p><p className="font-medium text-gray-700">{op.created_by_name}</p></div>
            <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Fecha</p><p className="font-medium text-gray-700">{formatDate(op.created_at)}</p></div>
            {op.approved_by_name && (
              <div><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Aprobado por</p><p className="font-medium text-emerald-700">{op.approved_by_name}</p></div>
            )}
            {/* Fix GT (2026-10-17): aprobaciones por perfil (las hechas y, si está pendiente, las que faltan). */}
            {op.status === "pendiente_aprobacion" && op.aprobacion_perfiles ? (
              <div className="col-span-2" data-testid="op-detail-perfiles">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Aprobación por perfiles · {formatPYG(op.aprobacion_perfiles.monto)} · {textoTramoOP(op.aprobacion_perfiles.tramo)}</p>
                <div className="flex"><ChipsAprobacionOP ap={op.aprobacion_perfiles} /></div>
              </div>
            ) : op.status !== "pendiente_aprobacion" && Object.keys(op.aprobaciones_op || {}).length > 0 && (
              <div className="col-span-2" data-testid="op-detail-aprobaciones">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Aprobaron</p>
                <p className="font-medium text-emerald-700 text-xs">{Object.values(op.aprobaciones_op).map(a => `${a.perfil}: ${a.by_name}`).join(" · ")}</p>
              </div>
            )}
            {/* Fix FR (2026-10-12): aprobación parcial y su saldo. */}
            {op.aprobacion_parcial && (
              <div className="col-span-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="op-detail-parcial">
                Aprobada por {formatPYG(op.aprobacion_parcial.monto_aprobado)} de {formatPYG(op.aprobacion_parcial.monto_original)}. El saldo de {formatPYG(op.aprobacion_parcial.saldo)} quedó en la OP {op.aprobacion_parcial.saldo_op_numero}.
              </div>
            )}
            {/* Fix HH (2026-10-20): pago parcial de una OP aprobada y su saldo. */}
            {op.pago_parcial && (
              <div className="col-span-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="op-detail-pago-parcial">
                Pagada por {formatPYG(op.pago_parcial.monto_pagado)} de {formatPYG(op.pago_parcial.monto_original)}. El saldo de {formatPYG(op.pago_parcial.saldo)} quedó en la OP {op.pago_parcial.saldo_op_numero}.
              </div>
            )}
            {op.es_saldo && (
              <div className="col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="op-detail-saldo">
                Saldo de la OP {op.op_origen_numero}: {op.status === "pendiente_aprobacion" ? "lo que quedó pendiente de una aprobación parcial." : "lo que quedó sin pagar de un pago parcial."}
              </div>
            )}
            {/* Fix CD (2026-09-10): el pago registrado, con su fecha y referencia. */}
            {op.status === "pagada" && (
              <div data-testid="op-detail-pagado">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Pagado</p>
                <p className="font-medium text-blue-700">
                  {formatDate(op.fecha_pago || op.closed_at)}{op.closed_by_name ? ` · ${op.closed_by_name}` : ""}{op.referencia_pago ? ` · Ref. ${op.referencia_pago}` : ""}
                </p>
              </div>
            )}
            {op.reject_reason && (
              <div className="col-span-2"><p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Motivo rechazo</p><p className="font-medium text-red-700">{op.reject_reason}</p></div>
            )}
          </div>

          {/* Items table */}
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide">Tipo</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide">Código</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide">N° Factura</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide">Proveedor</th>
                  <th className="px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wide">Monto</th>
                </tr>
              </thead>
              <tbody>
                {(op.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TYPE_BADGE[it.order_type] || "bg-gray-100 text-gray-700"}`}>{it.order_type}</span></td>
                    <td className="px-4 py-2.5 text-xs font-mono text-gray-700">{it.order_number}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600" title={(it.invoice_numbers || []).join(", ")}>
                      {invoiceNumbersLabel(it.invoice_numbers?.length ? it.invoice_numbers : (it.invoice_number ? [it.invoice_number] : []))}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700">{it.provider_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900">{formatPYG(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={4} className="px-4 py-2.5 text-sm font-bold text-right text-slate-700">TOTAL</td>
                  <td className="px-4 py-2.5 text-right font-bold text-slate-900">{formatPYG(op.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* BLOQUE C: Linked system documents (OC/AP/CRT) */}
          {!isOPE && linkedDocs && linkedDocs.linked_docs?.length > 0 && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-200">
                <LinkIcon size={13} className="text-gray-500" />
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Documentos Vinculados (Sistema)</p>
              </div>
              <div className="divide-y divide-gray-100">
                {linkedDocs.linked_docs.map((doc, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 px-4 py-2.5 ${doc.present && doc.detail ? "cursor-pointer hover:bg-blue-50/50 transition-colors" : ""}`}
                    onClick={() => { if (doc.present && doc.detail) setDetailDoc(doc); }}
                    data-testid={`linked-doc-${doc.tipo}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${doc.present ? "bg-emerald-100" : "bg-red-100"}`}>
                      {doc.present ? <CheckCircle2 size={13} className="text-emerald-600" /> : <XCircle size={13} className="text-red-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold ${doc.present ? "text-gray-800" : "text-red-700"} ${doc.present && doc.detail ? "underline decoration-dotted" : ""}`}>{doc.label}</p>
                      {!doc.present && doc.note && <p className="text-xs text-red-500 mt-0.5">{doc.note}</p>}
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${doc.present ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{doc.tipo}</span>
                  </div>
                ))}
              </div>
              {!loadingLinked && linkedDocs && !linkedDocs.can_approve && (
                <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
                  <ShieldAlert size={13} className="text-amber-600 flex-shrink-0" />
                  <p className="text-xs text-amber-700 font-semibold">Faltan documentos requeridos para aprobar esta OP</p>
                </div>
              )}
            </div>
          )}

          <ResumenConciliacionPago op={op} />

          {/* BLOQUE C: Uploaded documents */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Paperclip size={13} className="text-gray-500" />
                <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Documentos Adjuntos</p>
                {uploadedDocs.length + facturasCompras.length > 0 && <span className="text-xs bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5 font-bold">{uploadedDocs.length + facturasCompras.length}</span>}
              </div>
              {op.status !== "pagada" && (
                <button onClick={() => setShowUploadForm(v => !v)}
                  className="flex items-center gap-1 text-xs font-semibold text-[#2E6DA4] hover:text-[#245A8A] transition-colors">
                  <Upload size={12} /> Adjuntar
                </button>
              )}
            </div>

            {/* Upload form */}
            {showUploadForm && (
              <div className="px-4 py-3 bg-blue-50/30 border-b border-blue-100 space-y-2">
                <div className="flex gap-2">
                  <select value={uploadForm.doc_type} onChange={e => { setMulti([]); if (fileInputRef.current) fileInputRef.current.value = ""; setUploadForm(f => ({ ...f, doc_type: e.target.value })); }}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white">
                    <option value="factura">Factura</option>
                    <option value="comprobante_pago">Comprobante de Pago</option>
                    <option value="otro">Otro</option>
                  </select>
                  <input type="text" value={uploadForm.notes} onChange={e => setUploadForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Nota (opcional)..." className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                </div>
                <div className="flex items-center gap-2">
                  <input ref={fileInputRef} type="file" multiple={uploadForm.doc_type === "comprobante_pago"} accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} className="text-xs text-gray-600 flex-1" data-testid="op-doc-archivos" />
                  <button onClick={multi.length ? handleUploadMulti : handleUpload} disabled={uploading || (multi.length ? multiBloquea : (!uploadForm.doc_name || ia.bloquea))}
                    className="flex items-center gap-1 bg-[#2E6DA4] text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50 transition-all"
                    data-testid="op-doc-subir">
                    {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Subir
                  </button>
                </div>
                {multi.length > 0 && <>
                  {op.status !== "pagada" && <label className="block text-xs">Pago parcial (Gs., opcional; vacío = total de la OP)<input type="number" min="1" max={op.total} value={montoPagoAdj} onChange={e => setMontoPagoAdj(e.target.value)} className="block border rounded p-1" data-testid="op-doc-monto-parcial" /></label>}
                  <LoteComprobantes opId={op._id} archivos={multi} lecturas={multiLect} setLecturas={setMultiLect} alertasServidor={multiAlertas}
                    esperado={multiEsperado} previo={uploadedDocs.filter(x => x.doc.doc_type === "comprobante_pago").reduce((s, x) => s + montoDeDoc(x.doc), 0)}
                    grupoAlertas={grupoAl} grupoConfirmadas={grupoOk} setGrupoConfirmadas={setGrupoOk}
                    onRemove={id => setMulti(prev => prev.filter(x => x.id !== id))} />
                </>}
                <PanelLecturaIAPago ia={ia} />{/* Fix GR */}
              </div>
            )}

            {/* Fix GQ (2026-10-17): "Una vez vinculada la factura en compras de materiales/servicios,
                automáticamente el sistema puede cargar para la OP" (CEN). Las facturas de la OC o de la
                certificación llegan solas; se gestionan en Compras → Facturas. */}
            {facturasCompras.length > 0 && (
              <div className="divide-y divide-emerald-100 bg-emerald-50/40 border-b border-gray-100" data-testid="op-facturas-compras">
                {facturasCompras.map((f, i) => (
                  <div key={`${f.source_id}-${f.invoice_id}-${i}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`op-factura-compras-${i}`}>
                    <FileText size={14} className="text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate font-mono">{f.invoice_number}</p>
                      <p className="text-xs text-gray-500">Vinculada en Compras · {f.source_tipo} {f.source_number} · {formatPYG(f.invoice_total)}{f.invoice_date ? ` · ${formatDate(f.invoice_date)}` : ""}</p>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">Factura · Compras</span>
                    {f.doc_available ? (
                      <button type="button"
                        onClick={() => window.open(`${API}/purchases/oc-from-pf/${f.source_id}/invoices/${f.invoice_id}/document?inline=1`, "_blank")}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Ver factura"
                        data-testid={`op-factura-compras-ver-${i}`}>
                        <Eye size={13} />
                      </button>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic whitespace-nowrap">Sin archivo</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {uploadedDocs.length === 0 ? (
              facturasCompras.length === 0 && <div className="px-4 py-3 text-xs text-gray-400 text-center">Sin documentos adjuntos</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {uploadedDocs.map(({ doc, idx }) => (
                  <div key={doc.id || doc.sha256 || idx} data-testid={`op-doc-${idx}`}>
                  <div className="flex items-center gap-3 px-4 py-2.5">
                    <FileText size={14} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{doc.doc_name}</p>
                      <p className="text-xs text-gray-400">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type} · {doc.uploaded_by_name || ""}{doc.version > 1 ? ` · v${doc.version}` : ""}</p>
                      {doc.monto_transferencia != null && <p className="text-xs text-gray-600">{formatPYG(doc.monto_manual?.valor ?? doc.monto_transferencia)} · {formatDate(doc.fecha_transferencia)} · Ref. {doc.referencia_transferencia || "—"}</p>}
                      {doc.grupo?.n > 1 && <p className="text-[10px] text-blue-700" data-testid={`op-doc-grupo-${idx}`}>Comprobante {doc.grupo.parte} de {doc.grupo.n} · suma del grupo {formatPYG(doc.grupo.suma)}</p>}
                      {doc.monto_manual && <p className="text-[11px] text-amber-800" data-testid={`op-doc-monto-manual-${idx}`}>
                        Importe corregido a mano por {doc.monto_manual.by_name || "—"} el {formatDate(doc.monto_manual.at)}{doc.monto_manual.ia_leido ? ` · la IA había leído ${formatPYG(doc.monto_manual.ia_leido)}` : ""}</p>}
                      {/* Fix GR: lo que se confirmó al cargar */}
                      {doc.control_ia?.estado === "con_alertas" && (
                        <div className="mt-1 text-[11px] text-amber-800" data-testid={`op-doc-alertas-${idx}`}>
                          <ul className="list-disc pl-4">{(doc.control_ia.alertas || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
                          {doc.control_ia.confirmado_por && <p className="text-gray-500">Confirmado por {doc.control_ia.confirmado_por}</p>}
                        </div>
                      )}
                    </div>
                    <ControlIABadgePago c={doc.control_ia} />{/* Fix GR */}
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</span>
                    {/* Fix CD (2026-09-10): el listado ya no trae el archivo embebido;
                        se abre bajo demanda (mismo índice que usa el borrado). */}
                    {doc.tiene_archivo !== false && (
                      <button type="button"
                        onClick={() => window.open(`${API}/treasury/payment-orders/${op._id}/documents/${idx}/file?inline=1`, "_blank")}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Ver / descargar"
                        data-testid={`op-doc-ver-${idx}`}>
                        <Download size={13} />
                      </button>
                    )}
                    {puedeGestionarDocs && (
                      <>
                        <button type="button" onClick={() => setDocAccion({ idx, modo: "editar" })} className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Editar datos" data-testid={`op-doc-editar-${idx}`}>
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={() => setDocAccion({ idx, modo: "reemplazar" })} className="p-1 text-gray-400 hover:text-amber-600 transition-colors" title="Reemplazar archivo" data-testid={`op-doc-reemplazar-${idx}`}>
                          <RefreshCw size={13} />
                        </button>
                        <button type="button" onClick={() => setDocAccion({ idx, modo: "quitar" })} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Quitar de la OP" data-testid={`op-doc-quitar-${idx}`}>
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                  {docAccion?.idx === idx && (
                    <DocAccionPanel accion={docAccion.modo} op={op} doc={doc} idx={idx} onOp={handleDocActualizado} onClose={() => setDocAccion(null)} />
                  )}
                  </div>
                ))}
              </div>
            )}
            {docsRetirados.length > 0 && (
              <div className="border-t border-gray-100" data-testid="op-docs-retirados">
                <button type="button" onClick={() => setVerRetirados(v => !v)} className="w-full flex items-center gap-1.5 px-4 py-2 text-xs text-gray-500 hover:text-gray-700" data-testid="op-docs-retirados-toggle">
                  <History size={12} /> Historial de versiones retiradas ({docsRetirados.length})
                </button>
                {verRetirados && docsRetirados.map(({ doc, idx }) => (
                  <div key={doc.id || idx} className="flex items-center gap-3 px-4 py-1.5 bg-gray-50 text-gray-500" data-testid={`op-doc-retirado-${idx}`}>
                    <FileText size={13} className="flex-shrink-0" />
                    <p className="flex-1 min-w-0 truncate text-xs">{doc.doc_name} · retirado por {doc.retirado.by_name || "—"} el {formatDate(doc.retirado.at)}{doc.retirado.motivo ? ` · ${doc.retirado.motivo}` : ""}</p>
                    {doc.tiene_archivo !== false && (
                      <button type="button" onClick={() => window.open(`${API}/treasury/payment-orders/${op._id}/documents/${idx}/file?inline=1`, "_blank")} className="p-1 hover:text-blue-600" title="Ver versión retirada">
                        <Download size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* BLOQUE C: Close payment for OPE */}
          {isOPE && op.status === "aprobada" && (
            <div className={`rounded-xl border p-4 ${canClosePayment ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}`}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 text-gray-700">
                <DollarSign size={13} /> Cierre de Pago
              </p>
              {missingDocsForClose.length > 0 ? (
                <div className="flex items-start gap-2">
                  <ShieldAlert size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-amber-700 font-semibold">Documentos faltantes para cerrar el pago:</p>
                    <ul className="list-disc ml-4 mt-0.5">
                      {missingDocsForClose.map(d => <li key={d} className="text-xs text-amber-700">{d}</li>)}
                    </ul>
                    <p className="text-xs text-amber-600 mt-1">Adjunte los documentos requeridos usando el botón "Adjuntar" de arriba.</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-emerald-700 mb-3">Todos los documentos requeridos están adjuntos. Puede cerrar el pago.</p>
                  <button onClick={handleClosePayment} disabled={closingPayment}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60 transition-all"
                    data-testid="close-payment-btn">
                    {closingPayment ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Confirmar Pago y Cerrar OP
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => downloadOPPdf(op)} data-testid="op-detail-pdf-btn" className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors">
              <Download size={14} /> PDF
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  , document.body)}
      {detailDoc && <LinkedDocDetailModal doc={detailDoc} onClose={() => setDetailDoc(null)} />}
    </>
  );
}

// ── Registrar pago (Fix CD, 2026-09-10) ───────────────────────────────────────
// "procesar el cambio de estado de 'pendiente' a 'pagado' (…) debe ser necesaria
// la condición de subir a la plataforma el 'comprobante de transferencia'"
// (Sergio, 10/09). Sin comprobante (nuevo o ya adjunto) no se habilita el botón;
// el backend lo exige igual.
function ResumenConciliacionPago({ op }) {
  const c = op?.conciliacion_pago;
  if (!c || (!c.cantidad && op.status !== "pagada")) return null;
  return <div className={`rounded-lg border p-3 text-xs ${c.estado === "conciliado" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="op-conciliacion">
    <p className="font-semibold">{c.estado === "conciliado" ? "Comprobantes conciliados" : "Revisar conciliación del pago"}</p>
    <p>{c.cantidad} comprobante(s) · Suma: {formatPYG(c.total_comprobantes)} · OP: {formatPYG(c.total_op)}</p>
    {c.ajustes_manuales > 0 && <p data-testid="op-conciliacion-manual">Incluye {c.ajustes_manuales} importe(s) corregido(s) a mano, no el leído por IA.</p>}
    {c.monto_conciliado && c.alertas.length > 0 && <p>La suma coincide. Se conservan las alertas y confirmaciones de cada documento.</p>}
    {c.alertas.length > 0 && <ul className="list-disc pl-4 mt-1">{c.alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>}
    <p className="mt-1">La conciliación documental no modifica el movimiento de dinero registrado.</p>
  </div>;
}

function ComprobanteTransferenciaFila({ opId, archivo, onChange, onRemove, alertasServidor }) {
  const [monto, setMonto] = useState("");
  const [fecha, setFecha] = useState(todayAsuncion());
  const [referencia, setReferencia] = useState("");
  const montoLeido = useRef(null);
  const cambio = useRef(onChange); cambio.current = onChange;
  const ia = useLecturaIAPago(opId, { aplicar: a => {
    if (a.fecha) setFecha(a.fecha);
    if (a.referencia) setReferencia(a.referencia);
    if (a.monto && !monto) setMonto(String(a.monto));
  } });
  const { leer } = ia;
  const errorIA = useRef(ia.errorAlConfirmar); errorIA.current = ia.errorAlConfirmar;
  useEffect(() => { leer(archivo.base64, "comprobante_pago", null, true); }, [leer, archivo.base64]);
  useEffect(() => {
    if (alertasServidor) errorIA.current({ response: { status: 409, data: { detail: { alertas: alertasServidor } } } });
  }, [alertasServidor]);
  useEffect(() => {
    cambio.current({ comprobante_nombre: archivo.nombre, comprobante_base64: archivo.base64,
      monto: Number(monto), fecha_pago: fecha, referencia, ...ia.payload(), bloquea: ia.bloquea });
  }, [archivo.nombre, archivo.base64, monto, fecha, referencia, ia.res, ia.alertas, ia.confirmadas, ia.leyendo]);
  return <div className="border rounded-xl p-3 space-y-2" data-testid="pago-transferencia">
    <div className="flex justify-between gap-2"><p className="text-xs font-semibold">{archivo.nombre}</p><button type="button" onClick={onRemove} className="text-xs text-red-700">Quitar</button></div>
    <div className="grid grid-cols-3 gap-2 text-xs">
      <label>Importe (Gs.)<input type="number" min="1" value={monto} onChange={e => setMonto(e.target.value)} onBlur={() => {
        const n = Number(monto); if (n > 0 && n !== montoLeido.current) { montoLeido.current = n; leer(archivo.base64, "comprobante_pago", n, true); }
      }} className="w-full border rounded p-1" /></label>
      <label>Fecha<input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="w-full border rounded p-1" /></label>
      <label>Referencia<input value={referencia} onChange={e => setReferencia(e.target.value)} className="w-full border rounded p-1" /></label>
    </div>
    <PanelLecturaIAPago ia={ia} />
  </div>;
}

function recortarLote(files, ya) {
  const cupo = Math.max(0, MAX_COMPROBANTES - ya);
  if (files.length <= cupo) return files;
  toast.warning(`Se admiten hasta ${MAX_COMPROBANTES} comprobantes por operación. Se agregaron ${cupo}; no se agregaron: ${files.slice(cupo).map(f => f.name).join(", ")}`);
  return files.slice(0, cupo);
}

const leerArchivosLote = (files) => Promise.all(files.map(file => new Promise((resolve, reject) => {
  const r = new FileReader(); r.onerror = () => reject(new Error("No se pudo leer el archivo"));
  r.onload = () => resolve({ id: crypto.randomUUID(), nombre: file.name, base64: r.result }); r.readAsDataURL(file);
})));

const montoDeDoc = (d) => Number(d?.monto_manual?.valor ?? d?.monto_transferencia ?? 0) || 0;

function GrupoComprobantesResumen({ archivos, lista, esperado, previo, alertas, confirmadas, onConfirmar }) {
  if (archivos.length < 2) return null;
  const filas = archivos.map((a, i) => ({ nombre: a.nombre, monto: Number(lista[i]?.monto) > 0 ? Number(lista[i].monto) : 0 }));
  const suma = filas.reduce((s, f) => s + f.monto, 0);
  const total = suma + previo;
  const completo = filas.every(f => f.monto > 0);
  const coincide = completo && Math.abs(total - esperado) <= 1;
  return <div className={`rounded-lg border p-3 text-xs space-y-1 ${coincide ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="pago-grupo-resumen">
    <p className="font-semibold" data-testid="pago-grupo-estado">{archivos.length} comprobantes · Suma: {formatPYG(total)}{previo > 0 ? ` (incluye ${formatPYG(previo)} ya adjuntos)` : ""} · Esperado: {formatPYG(esperado)} · {coincide ? "Coincide" : completo ? "No coincide" : "Faltan importes"}</p>
    <ul className="list-disc pl-4">{filas.map((f, i) => <li key={archivos[i].id} data-testid={`pago-grupo-parte-${i}`}>Parte {i + 1}: {f.nombre} — {f.monto > 0 ? formatPYG(f.monto) : "sin importe"}</li>)}</ul>
    <p className="text-[10px]">La suma definitiva la calcula el servidor desde cada comprobante al guardar.</p>
    {alertas.length > 0 && <div className="pt-1" data-testid="pago-grupo-alertas">
      <ul className="list-disc pl-4">{alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>
      <label className="flex items-start gap-2 pt-1 cursor-pointer font-semibold"><input type="checkbox" checked={confirmadas} onChange={e => onConfirmar(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" data-testid="pago-grupo-confirmar" />Revisé la suma de los comprobantes y confirmo igual</label>
    </div>}
  </div>;
}

function LoteComprobantes({ opId, archivos, lecturas, setLecturas, onRemove, alertasServidor, esperado, previo, grupoAlertas, grupoConfirmadas, setGrupoConfirmadas }) {
  return <>
    {archivos.map(a => <ComprobanteTransferenciaFila key={a.id} opId={opId} archivo={a}
      alertasServidor={alertasServidor[a.id]}
      onChange={d => setLecturas(prev => ({ ...prev, [a.id]: d }))}
      onRemove={() => onRemove(a.id)} />)}
    <GrupoComprobantesResumen archivos={archivos} lista={archivos.map(a => lecturas[a.id])} esperado={esperado} previo={previo}
      alertas={grupoAlertas} confirmadas={grupoConfirmadas} onConfirmar={setGrupoConfirmadas} />
  </>;
}

function RegistrarPagoModal({ op, onConfirm, onClose }) {
  const [archivos, setArchivos] = useState([]);
  const [lecturas, setLecturas] = useState({});
  const [leyendoArchivos, setLeyendoArchivos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [monto, setMonto] = useState(String(op?.total ?? ""));
  const [fechaPago, setFechaPago] = useState(todayAsuncion());
  const [error, setError] = useState("");
  const [alertasServidor, setAlertasServidor] = useState({});
  const [grupoAlertas, setGrupoAlertas] = useState([]);
  const [grupoConfirmadas, setGrupoConfirmadas] = useState(false);
  const completar = op?.status === "pagada";
  const pendiente = op?.status === "pendiente_aprobacion";
  const parcialOk = !completar && (pendiente || op?.status === "aprobada") && !op?.source;
  const montoNum = Number(monto);
  const esParcial = parcialOk && montoNum > 0 && montoNum < Number(op?.total || 0);
  const existentes = (op?.documents || []).filter(d => d.doc_type === "comprobante_pago" && !d.retirado);
  const elegirArchivos = async e => {
    const files = recortarLote(Array.from(e.target.files || []), archivos.length); e.target.value = "";
    if (!files.length) return;
    if (files.some(f => f.size > 5 * 1024 * 1024)) { toast.error("Cada comprobante admite hasta 5 MB"); return; }
    setLeyendoArchivos(true);
    try {
      const nuevos = await leerArchivosLote(files);
      setArchivos(prev => [...prev, ...nuevos]); setError("");
    } catch (err) { toast.error(err.message); } finally { setLeyendoArchivos(false); }
  };
  const lista = archivos.map(a => lecturas[a.id]);
  const sumaDeclarada = lista.reduce((s, d) => s + (Number(d?.monto) || 0), 0);
  useEffect(() => { setGrupoAlertas([]); setGrupoConfirmadas(false); }, [archivos.length, sumaDeclarada, montoNum, esParcial]);
  const bloquea = leyendoArchivos || lista.some(d => !d || d.bloquea || !Number.isFinite(d.monto) || d.monto <= 0) || (grupoAlertas.length > 0 && !grupoConfirmadas);
  const confirmar = async () => {
    if (bloquea || (!archivos.length && (completar || !existentes.length))) return;
    if (parcialOk && (!Number.isFinite(montoNum) || montoNum <= 0 || montoNum > Number(op.total))) { setError("Revisá el monto a pagar"); return; }
    setEnviando(true); setError("");
    try {
      await onConfirm({ comprobantes: lista.map(({ bloquea: _, ...d }) => d),
        ...(grupoConfirmadas ? { alertas_grupo_confirmadas: grupoAlertas } : {}),
        ...(!completar ? { fecha_pago: fechaPago } : {}),
        ...(esParcial ? { monto: montoNum } : {}) });
    } catch (err) {
      const det = err.response?.data?.detail;
      if (err.response?.status === 409 && det?.grupo) { setGrupoAlertas(det.alertas || []); setGrupoConfirmadas(false); }
      if (err.response?.status === 409 && Number.isInteger(det?.comprobante) && archivos[det.comprobante]) {
        setAlertasServidor(prev => ({ ...prev, [archivos[det.comprobante].id]: det.alertas || [] }));
      }
      // El control del servidor puede detectar cambios; no confirma alertas automáticamente.
      setError(typeof det === "string" ? det : [det?.mensaje, ...(det?.alertas || [])].filter(Boolean).join(" · ") || "No se pudo guardar");
    } finally { setEnviando(false); }
  };
  return createPortal(<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="registrar-pago-modal">
    <div className="bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
      <h3 className="font-bold text-lg">{completar ? "Completar comprobantes" : "Registrar pago"} · {op?.number}</h3>
      {completar ? <p className="text-xs text-blue-800">Agregá las transferencias faltantes al pago existente. Se conserva la trazabilidad y no se genera otro movimiento.</p>
        : pendiente && <p className="text-xs text-amber-800">Se mantienen las aprobaciones necesarias antes de registrar el pago.</p>}
      <ResumenConciliacionPago op={op} />
      {parcialOk && <label className="block text-xs">Monto total a pagar (Gs.)<input type="number" min="1" max={op.total} value={monto} onChange={e => setMonto(e.target.value)} className="block border rounded p-2" />
        {esParcial && <span>El saldo de {formatPYG(op.total - montoNum)} queda en una OP nueva según el flujo vigente.</span>}</label>}
      {!completar && <label className="block text-xs">Fecha del pago registrado<input type="date" value={fechaPago} onChange={e => setFechaPago(e.target.value)} className="block border rounded p-2" /></label>}
      {existentes.length > 0 && <p className="text-xs text-gray-600">Ya hay {existentes.length} comprobante(s); se conservan. Adjuntá sólo los faltantes.</p>}
      <label className="block text-xs font-semibold">Comprobantes de transferencia (podés elegir varios)<input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={elegirArchivos} disabled={enviando || leyendoArchivos} className="block mt-1" /></label>
      {archivos.length > 0 && <LoteComprobantes opId={op._id} archivos={archivos} lecturas={lecturas} setLecturas={setLecturas}
        alertasServidor={alertasServidor} esperado={esParcial ? montoNum : Number(op?.total || 0)} previo={existentes.reduce((s, d) => s + montoDeDoc(d), 0)}
        grupoAlertas={grupoAlertas} grupoConfirmadas={grupoConfirmadas} setGrupoConfirmadas={setGrupoConfirmadas}
        onRemove={id => { setArchivos(prev => prev.filter(x => x.id !== id)); setError(""); }} />}
      {lista.filter(Boolean).length > 0 && <p className="text-xs">Nuevas transferencias declaradas: {formatPYG(lista.reduce((s, d) => s + (d?.monto || 0), 0))}. La conciliación considera también los comprobantes ya guardados.</p>}
      {error && <p role="alert" className="text-xs text-red-700 bg-red-50 p-2">{error}</p>}
      <div className="flex justify-end gap-2"><button onClick={onClose} disabled={enviando}>Cancelar</button><button onClick={confirmar}
        disabled={enviando || bloquea || (!archivos.length && (completar || !existentes.length))} className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50">
        {enviando ? "Guardando…" : completar ? "Completar comprobantes" : "Registrar pago"}</button></div>
    </div>
  </div>, document.body);
}


// ── Aprobar OP (Fix FR, 2026-10-12) ───────────────────────────────────────────
// "al momento de aprobar, permitir aprobaciones parciales. Es decir si el monto era 8.750.000gs y
// solo se pagará 5.000.000gs (…) el ERP haga la resta y deje como pendiente de aprobación la
// diferencia" (HEGS). Con el monto completo se aprueba como siempre.
function AprobarOPModal({ op, onConfirm, onClose }) {
  const ap = op?.aprobacion_perfiles;   // Fix GT (2026-10-17): perfiles de aprobación de Pagos
  const [monto, setMonto] = useState(String(ap?.monto ?? op?.total ?? ""));
  const [enviando, setEnviando] = useState(false);
  const total = Number(op?.total) || 0;
  const montoNum = Number(monto) || 0;
  const parcialOk = !op?.source;   // las OP que vienen de otro módulo se aprueban completas
  const valido = montoNum > 0 && montoNum <= total;
  const esParcial = valido && montoNum < total;
  const confirmar = async () => {
    if (!valido) { toast.error("El monto tiene que estar entre 1 y el total de la OP"); return; }
    setEnviando(true);
    try { await onConfirm(esParcial ? montoNum : null); } finally { setEnviando(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="aprobar-op-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Aprobar Orden de Pago</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="font-mono font-semibold text-gray-700">{op?.number}</span> · Total <span className="font-semibold text-gray-800">{formatPYG(total)}</span>
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-600">Monto a aprobar (Gs.)</label>
          <input type="number" min="1" max={total || undefined} step="1" value={monto} disabled={!parcialOk} autoFocus
            onChange={e => setMonto(e.target.value)} onKeyDown={e => { if (e.key === "Enter") confirmar(); }}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-50" data-testid="aprobar-op-monto" />
          {!parcialOk && <p className="text-xs text-gray-500">Esta OP viene de otro módulo y se aprueba completa.</p>}
          {esParcial && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="aprobar-op-saldo">
              Se aprueban {formatPYG(montoNum)}. Los {formatPYG(total - montoNum)} restantes quedan en una OP nueva, pendiente de aprobación.
            </p>
          )}
        </div>
        {/* Fix GT (2026-10-17) */}
        {ap && (
          <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 space-y-1" data-testid="aprobar-op-perfiles">
            <p><span className="font-semibold">Perfiles que aprueban</span> ({formatPYG(ap.monto)} · {textoTramoOP(ap.tramo)}):</p>
            <ChipsAprobacionOP ap={ap} testId="aprobar-op-chips" />
            {ap.puede_finalizar && <p className="text-slate-500">Tu firma guardada ya acredita los requisitos. Podés finalizar la aprobación sin registrar otra firma.</p>}
            {ap.mio && (
              <p className="text-slate-500">
                Aprobás como {ap.mio.nombre}.{ap.faltan.some(p => !(ap.mio.cubre || [ap.mio.id]).includes(p.id)) ? " La OP queda aprobada cuando aprueben todos." : " Con tu aprobación queda aprobada."}
                {Object.keys(ap.vigentes || {}).length > 0 ? ` Para sumar tu aprobación a las que ya están, dejá el monto en ${formatPYG(ap.monto)}.` : ""}
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={enviando}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50">Cancelar</button>
          <button onClick={confirmar} disabled={!valido || enviando}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="aprobar-op-confirmar">
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {esParcial ? "Aprobar parcial" : "Aprobar"}
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Reject Modal ──────────────────────────────────────────────────────────────
// Fix IL (2026-10-30): "A lo sumo el sistema debe dar alerta si encuentra coincidencias significativas en las OCs y las
// operaciones en caja chica" (CEN). Solo avisa: no cierra ni vincula nada.
function AvisoCajaChica({ items, testId }) {
  if (!items?.length) return null;
  return (
    <p className="text-[11px] text-amber-700 mt-0.5 font-sans font-normal" data-testid={testId}
      title={items.map((c) => `${c.numero} · ${formatDate(c.fecha)} · ${c.factura || "sin factura"} · ${formatPYG(c.monto)} (${c.motivo})`).join("\n")}>
      Posible pago por Caja Chica: {items.map((c) => `${c.numero} (${c.motivo})`).join(", ")}
    </p>
  );
}

function RejectModal({ op, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  // Fix IL (2026-10-30): "se debe cerrar en tesorería al rechazar la OP, de manera manual. Al «darse de baja» la OC por su
  // rechazo se evitan las situaciones mencionadas" (CEN). Marcado, la OC no vuelve a quedar para pagar.
  const [darDeBaja, setDarDeBaja] = useState(false);
  const tieneOC = (op?.items || []).some((i) => (i.order_type || "OC") === "OC" && i.order_id);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Rechazar Orden de Pago</h3>
        <p className="text-sm text-gray-500 mb-4">OP: <span className="font-mono font-semibold text-gray-700">{op?.number}</span></p>
        {op?.coincidencias_caja_chica?.length > 0 && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="reject-coincidencias-cc">
            {op.coincidencias_caja_chica.map((c) => (
              <p key={c.id} className="text-xs text-amber-800">Caja Chica {c.numero} · {formatDate(c.fecha)} · {c.factura || "sin factura"} · {formatPYG(c.monto)} ({c.motivo})</p>
            ))}
          </div>
        )}
        <textarea
          className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-all"
          rows={3}
          placeholder={darDeBaja ? "Motivo de la baja (por ejemplo, el gasto de Caja Chica con que se pagó)..." : "Motivo del rechazo (opcional)..."}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="reject-reason-input"
        />
        {tieneOC && (
          <label className="flex items-start gap-2 mt-3 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="mt-0.5 rounded" checked={darDeBaja} onChange={(e) => setDarDeBaja(e.target.checked)} data-testid="reject-dar-de-baja" />
            <span>Dar de baja la OC: ya se pagó (por ejemplo, por Caja Chica). No vuelve a quedar para pagar ni en Cuentas por pagar.</span>
          </label>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancelar</button>
          <button onClick={() => onConfirm(reason, darDeBaja)} disabled={darDeBaja && !reason.trim()} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50" data-testid="confirm-reject-btn">{darDeBaja ? "Rechazar y dar de baja" : "Rechazar"}</button>
        </div>
      </div>
    </div>
  , document.body);
}

// ── Contado Tab ───────────────────────────────────────────────────────────────
function ContadoTab({ eligible, loadingEligible, projects, onRefresh }) {
  const [search, setSearch] = useState("");
  const [generating, setGenerating] = useState(null);

  const filtered = eligible.filter((o) =>
    !search ||
    o.order_number?.toLowerCase().includes(search.toLowerCase()) ||
    o.provider_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.invoice_number?.toLowerCase().includes(search.toLowerCase())
  );

  const handleGenerate = async (order) => {
    setGenerating(order._id);
    try {
      const res = await axios.post(`${API}/treasury/payment-orders`, {
        type: "contado",
        project_id: order.project_id,
        items: [{
          order_type: order.order_type,
          order_id: order._id,
          order_number: order.order_number,
          invoice_number: order.invoice_number,
          amount: order.amount,
          iva_amount: order.iva_amount,
          provider_name: order.provider_name,
          project_name: order.project_name,
        }],
      }, { withCredentials: true });
      toast.success(`Orden de Pago ${res.data.number} generada`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar Orden de Pago");
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
            placeholder="Buscar OC/OTS, proveedor, factura..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="contado-search"
          />
        </div>
        <button onClick={onRefresh} className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 transition-colors" title="Actualizar">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Órdenes disponibles para pago</p>
          <span className="text-xs text-gray-400">{filtered.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Tipo</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Código</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proveedor</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proyecto</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">N° Factura</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Monto</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loadingEligible ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                    <Banknote size={32} className="mx-auto mb-2 opacity-20" />
                    <p>No hay órdenes disponibles para pago</p>
                    <p className="text-xs mt-1">Las OCs y OTSs aprobadas al contado aparecerán aquí</p>
                  </td>
                </tr>
              ) : (
                filtered.map((o) => (
                  <tr key={o._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${TYPE_BADGE[o.order_type]}`}>{o.order_type}</span>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs font-semibold text-gray-700">{o.order_number}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-700">{o.provider_name || "—"}<AvisoCajaChica items={o.coincidencias_caja_chica} testId={`contado-coincide-cc-${o._id}`} /></td>
                    <td className="px-5 py-3.5 text-sm text-gray-500">{o.project_name || "—"}</td>
                    <td className="px-5 py-3.5 text-xs text-gray-600 font-mono" title={(o.invoice_numbers || []).join(", ")}>
                      {invoiceNumbersLabel(o.invoice_numbers?.length ? o.invoice_numbers : (o.invoice_number ? [o.invoice_number] : []))}
                    </td>
                    <td className="px-5 py-3.5 text-right font-bold text-gray-900">{formatPYG(o.amount)}</td>
                    <td className="px-5 py-3.5 text-center">
                      <button
                        onClick={() => handleGenerate(o)}
                        disabled={generating === o._id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 mx-auto"
                        data-testid={`gen-op-contado-${o._id}`}
                      >
                        {generating === o._id ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                        Generar OP
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Crédito Tab ───────────────────────────────────────────────────────────────
function CreditoTab({ eligible, loadingEligible, projects, onRefresh }) {
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [pagarHasta, setPagarHasta] = useState("");

  const filtered = eligible.filter((o) => {
    const matchSearch = !search ||
      o.provider_name?.toLowerCase().includes(search.toLowerCase()) ||
      o.order_number?.toLowerCase().includes(search.toLowerCase());
    // Fix CE (2026-09-10): los filtros de fecha no se aplicaban.
    const fecha = String(o.fecha_factura || o.invoice_linked_at || o.created_at || "").slice(0, 10);   // Fix FO: fecha de la factura
    const matchFecha = (!dateFrom || fecha >= dateFrom) && (!dateTo || fecha <= dateTo);
    return matchSearch && matchFecha;
  // Fix FO (2026-10-09): primero las más vencidas.
  }).sort((a, b) => (a.dias_para_vencer ?? 1e9) - (b.dias_para_vencer ?? 1e9));

  // Fix FO (2026-10-09): "acá es donde uno va cargando de acuerdo al monto (…) que espera pagar
  // 15 millones (…) acá es donde uno genera la orden de pago" (CEN, reunión de Finanzas).
  // Marca las facturas de la lista, de la más vencida a la más nueva, hasta ese monto.
  const seleccionarHasta = () => {
    const tope = Number(String(pagarHasta).replace(/\D/g, "")) || 0;
    if (!tope) { toast.error("Ingresá el monto a pagar"); return; }
    const ids = [];
    let acum = 0;
    for (const o of filtered) {
      if (acum + (o.amount || 0) > tope) break;
      acum += o.amount || 0;
      ids.push(o._id);
    }
    setSelected(ids);
    if (!ids.length) toast.error("La factura más vencida supera ese monto");
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map((o) => o._id));
  };

  const selectedItems = eligible.filter((o) => selected.includes(o._id));
  const selectedTotal = selectedItems.reduce((a, o) => a + (o.amount || 0), 0);

  const handleGenerate = async () => {
    if (selectedItems.length === 0) {
      toast.error("Seleccione al menos una orden");
      return;
    }
    // Group by project_id — use first item's project
    const projectId = selectedItems[0].project_id;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/treasury/payment-orders`, {
        type: "credito",
        project_id: projectId,
        items: selectedItems.map((o) => ({
          order_type: o.order_type,
          order_id: o._id,
          order_number: o.order_number,
          invoice_number: o.invoice_number,
          amount: o.amount,
          iva_amount: o.iva_amount,
          provider_name: o.provider_name,
          project_name: o.project_name,
        })),
      }, { withCredentials: true });
      toast.success(`Orden de Pago ${res.data.number} generada con ${selectedItems.length} ítem(s)`);
      setSelected([]);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar Orden de Pago");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Proveedor</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
                placeholder="Buscar proveedor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="credito-provider-search"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">F. Inicio</label>
            <input type="date" className="py-2 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="credito-date-from" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">F. Final</label>
            <input type="date" className="py-2 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="credito-date-to" />
          </div>
          <button onClick={onRefresh} className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors" title="Actualizar">
            <RefreshCw size={14} />
          </button>
          {/* Fix FO (2026-10-09) */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Pagar hasta (Gs.)</label>
            <div className="flex gap-2">
              <input inputMode="numeric" className="w-36 py-2 px-3 text-sm border border-gray-200 rounded-lg bg-white" placeholder="15.000.000"
                value={pagarHasta} onChange={(e) => setPagarHasta(e.target.value)} data-testid="credito-pagar-hasta" />
              <button onClick={seleccionarHasta} className="px-3 py-2 text-sm font-semibold border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50"
                data-testid="credito-pagar-hasta-btn">Seleccionar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Selection summary */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">{selected.length} ítem(s) seleccionado(s)</span>
            <span className="text-sm text-emerald-700">— Total: <strong>{formatPYG(selectedTotal)}</strong></span>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="gen-op-credito-btn"
          >
            {generating ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={13} />}
            Generar OP Crédito
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" className="rounded" checked={filtered.length > 0 && selected.length === filtered.length} onChange={toggleAll} data-testid="select-all-checkbox" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Código</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proveedor</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proyecto</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">N° Factura</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">F. Factura</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Vencimiento</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Estado</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Monto</th>
              </tr>
            </thead>
            <tbody>
              {loadingEligible ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 10 }).map((__, j) => <td key={j} className="px-4 py-4"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-400 text-sm">
                    <CreditCard size={32} className="mx-auto mb-2 opacity-20" />
                    <p>No hay órdenes disponibles</p>
                  </td>
                </tr>
              ) : (
                filtered.map((o) => {
                  const isSelected = selected.includes(o._id);
                  return (
                    <tr key={o._id} className={`border-b border-gray-50 transition-colors cursor-pointer ${isSelected ? "bg-emerald-50/50" : "hover:bg-gray-50/50"}`} onClick={() => toggleSelect(o._id)}>
                      <td className="px-4 py-3.5 text-center">
                        <input type="checkbox" className="rounded" checked={isSelected} onChange={() => toggleSelect(o._id)} onClick={(e) => e.stopPropagation()} data-testid={`checkbox-${o._id}`} />
                      </td>
                      <td className="px-4 py-3.5"><span className={`text-xs px-2.5 py-1 rounded-full font-bold ${TYPE_BADGE[o.order_type]}`}>{o.order_type}</span></td>
                      <td className="px-4 py-3.5 font-mono text-xs font-semibold text-gray-700">{o.order_number}</td>
                      <td className="px-4 py-3.5 text-sm text-gray-700">{o.provider_name || "—"}<AvisoCajaChica items={o.coincidencias_caja_chica} testId={`credito-coincide-cc-${o._id}`} /></td>
                      <td className="px-4 py-3.5 text-sm text-gray-500">{o.project_name || "—"}</td>
                      <td className="px-4 py-3.5 text-xs text-gray-600 font-mono" title={(o.invoice_numbers || []).join(", ")}>
                        {invoiceNumbersLabel(o.invoice_numbers?.length ? o.invoice_numbers : (o.invoice_number ? [o.invoice_number] : []))}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-testid={`credito-ffact-${o._id}`}>{o.fecha_factura ? formatDate(o.fecha_factura) : "—"}</td>
                      {/* Fix CE (2026-09-10) */}
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-testid={`credito-vence-${o._id}`}>{o.vencimiento ? formatDate(o.vencimiento) : "—"}</td>
                      <td className="px-4 py-3.5 text-center" data-testid={`credito-estado-${o._id}`}>
                        {o.dias_para_vencer == null ? <span className="text-xs text-gray-400">—</span>
                          : o.dias_para_vencer < 0 ? <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-red-100 text-red-700">Vencida hace {-o.dias_para_vencer} d</span>
                          : o.dias_para_vencer === 0 ? <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-red-100 text-red-700">Vence hoy</span>
                          : <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${o.dias_para_vencer <= 15 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Vence en {o.dias_para_vencer} d</span>}
                      </td>
                      <td className="px-4 py-3.5 text-right font-bold text-gray-900">{formatPYG(o.amount)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Payment Orders Tab ─────────────────────────────────────────────────────────
// Fix CE (2026-09-10): proveedor(es) de la OP; en un pago directo, el beneficiario.
const opProveedor = (op) => {
  const nombres = [...new Set((op.items || []).map((i) => (i.provider_name || "").trim()).filter(Boolean))];
  return nombres.length ? nombres.join(", ") : (op.beneficiary || op.worker_name || "");
};

function OrdenesTab({ paymentOrders, loadingOPs, onApprove, onRejectClick, onView, canApprove, canPay, onPay, onRehabilitarOC }) {
  const [filterStatus, setFilterStatus] = useState("todos");
  const [search, setSearch] = useState("");

  const filtered = paymentOrders.filter((op) => {
    const matchStatus = filterStatus === "todos" || op.status === filterStatus;
    const matchSearch = !search ||
      op.number?.toLowerCase().includes(search.toLowerCase()) ||
      op.project_name?.toLowerCase().includes(search.toLowerCase()) ||
      opProveedor(op).toLowerCase().includes(search.toLowerCase()); // Fix CE (2026-09-10)
    return matchStatus && matchSearch;
  }).sort((a, b) => {
    // BLOQUE 50 PARTE B — órdenes pendientes de aprobación arriba; dentro de cada
    // grupo, la más nueva primero.
    const pa = a.status === "pendiente_aprobacion" ? 0 : 1;
    const pb = b.status === "pendiente_aprobacion" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  const pendingCount = paymentOrders.filter((op) => op.status === "pendiente_aprobacion").length;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 transition-all"
            placeholder="Buscar por código, proyecto o proveedor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ordenes-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "todos", label: "Todas" },
            { key: "pendiente_aprobacion", label: `Pendientes${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
            { key: "aprobada", label: "Aprobadas" },
            { key: "rechazada", label: "Rechazadas" },
            // Fix CD (2026-09-10): con el pago registrable, las pagadas se filtran aparte.
            { key: "pagada", label: "Pagadas" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all ${filterStatus === f.key ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">N° OP</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Tipo</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proveedor</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Proyecto</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Ítems</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Total</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Estado</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Fecha</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loadingOPs ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 9 }).map((__, j) => <td key={j} className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                    <FileText size={32} className="mx-auto mb-2 opacity-20" />
                    <p>No hay órdenes de pago</p>
                  </td>
                </tr>
              ) : (
                filtered.map((op) => {
                  const st = estadoDocumentalPago(op) || STATUS_MAP[op.status] || { label: op.status, cls: "bg-gray-100 text-gray-600 border-gray-200" };
                  const isPending = op.status === "pendiente_aprobacion";
                  // Fix GT (2026-10-17): con perfiles de aprobación, aprueba quien tiene el perfil que falta y
                  // PAGAR (aprobar y pagar) solo con la última aprobación.
                  const ap = isPending ? op.aprobacion_perfiles : null;
                  const puedeAprobar = ap ? (!!ap.mio || !!ap.puede_finalizar) : canApprove;
                  const puedeRechazar = canApprove || !!ap?.es_aprobador;
                  const puedePagarPendiente = puedeAprobar && canPay && (!ap || ap.puede_finalizar || ap.faltan.every(p => (ap.mio?.cubre || [ap.mio?.id]).includes(p.id)));
                  return (
                    <tr key={op._id} className="border-b border-gray-50 hover:bg-gray-50/30 transition-colors">
                      <td className="px-5 py-3.5 font-mono text-xs font-semibold text-slate-700">
                        {op.number}
                        {/* Fix FR (2026-10-12) */}
                        {op.es_saldo && <p className="font-sans font-normal text-[10px] text-amber-700" data-testid={`op-saldo-${op._id}`}>Saldo de {op.op_origen_numero}</p>}
                        {op.aprobacion_parcial && <p className="font-sans font-normal text-[10px] text-emerald-700" data-testid={`op-parcial-${op._id}`}>Aprobada parcial (de {formatPYG(op.aprobacion_parcial.monto_original)})</p>}
                        {op.pago_parcial && <p className="font-sans font-normal text-[10px] text-emerald-700" data-testid={`op-pago-parcial-${op._id}`}>Pago parcial (de {formatPYG(op.pago_parcial.monto_original)})</p>}{/* Fix HH */}
                        <AvisoCajaChica items={op.coincidencias_caja_chica} testId={`op-coincide-cc-${op._id}`} />{/* Fix IL */}
                        {op.bajas_oc?.length > 0 && <p className="font-sans font-normal text-[10px] text-gray-500" data-testid={`op-baja-oc-${op._id}`}>{op.bajas_oc_rehabilitadas ? "OC habilitada otra vez para pagar" : `OC dada de baja: ${op.bajas_oc.map((b) => b.number).join(", ")}`}</p>}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${op.type === "contado" ? "bg-blue-50 text-blue-700" : op.type === "directo" ? "bg-indigo-100 text-indigo-700" : "bg-purple-50 text-purple-700"}`}>
                          {op.type === "contado" ? "OP-M" : op.type === "directo" ? "DIRECTO" : "OP-C"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-700 max-w-[220px] truncate" title={opProveedor(op)} data-testid={`op-proveedor-${op._id}`}>{opProveedor(op) || "—"}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{op.project_name || "—"}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-500">{(op.items || []).length} ítem(s)</td>
                      <td className="px-5 py-3.5 text-right font-bold text-gray-900">{formatPYG(op.total)}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${st.cls}`}>{st.label}</span>
                        {op.conciliacion_pago?.estado !== "conciliado" && op.conciliacion_pago?.cantidad > 0 && <p className="text-[10px] text-amber-800 mt-1">{op.conciliacion_pago.alertas.length} alerta(s) · revisar comprobantes</p>}
                        <ChipsAprobacionOP ap={ap} compacto testId={`op-aprobaciones-${op._id}`} />{/* Fix GT */}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-400 whitespace-nowrap">{formatDate(op.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 justify-center">
                          <button onClick={() => onView(op)} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors" title="Ver" data-testid={`view-op-${op._id}`}>
                            <Eye size={13} />
                          </button>
                          <button onClick={() => downloadOPPdf(op)} className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors" title="PDF" data-testid={`pdf-op-${op._id}`}>
                            <Download size={13} />
                          </button>
                          {op.status === "pagada" && canPay && <button onClick={() => onPay(op)} className="px-2 py-1.5 border border-blue-300 text-blue-800 rounded-lg text-xs font-semibold" data-testid={`completar-op-${op._id}`}>Completar comprobantes</button>}
                          {isPending && puedeAprobar && (
                            <button
                              onClick={() => onApprove(op)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                              data-testid={`approve-op-${op._id}`}
                            >
                              <CheckCircle2 size={11} /> APROB
                            </button>
                          )}
                          {isPending && puedeRechazar && (
                            <button
                              onClick={() => onRejectClick(op)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
                              data-testid={`reject-op-${op._id}`}
                            >
                              <XCircle size={11} /> RECH
                            </button>
                          )}
                          {/* Fix CD (2026-09-10): registrar el pago con su comprobante. En
                              una pendiente la aprueba y la paga en un solo paso (solo quien
                              puede aprobar); en una aprobada, quien puede pagar. */}
                          {op.status === "rechazada" && op.bajas_oc?.length > 0 && !op.bajas_oc_rehabilitadas && canApprove && (   /* Fix IL: deshacer una baja hecha por error */
                            <button
                              onClick={() => onRehabilitarOC(op)}
                              className="px-2.5 py-1.5 border border-gray-300 text-gray-600 text-xs font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                              title="La OC vuelve a quedar para pagar"
                              data-testid={`rehabilitar-oc-${op._id}`}
                            >
                              Rehabilitar OC
                            </button>
                          )}
                          {((isPending && puedePagarPendiente) || (op.status === "aprobada" && canPay)) && (   /* Fix GT */
                            <button
                              onClick={() => onPay(op)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                              data-testid={`pay-op-${op._id}`}
                            >
                              <DollarSign size={11} /> PAGAR
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Fix CF (2026-09-10): solicitudes de pago que llegan de Compras (Servicios, Operatividad),
// Obras (certificados de contratistas) y RRHH (jornaleros). "Desde acá se generan las OPs".
const esSolicitud = (op) => ["solicitud", "pendiente"].includes(op.status);
const ORIGENES = [
  { key: "todos", label: "Todas" }, { key: "servicios", label: "Servicios" },
  { key: "operatividad", label: "Operatividad" }, { key: "contratistas", label: "Contratistas" },
  { key: "jornaleros", label: "Jornaleros" },
];

function SolicitudesPago({ onRefresh }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [origen, setOrigen] = useState("todos");
  const [busy, setBusy] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/treasury/solicitudes-pago`, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar las solicitudes de pago"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const generar = async (r) => {
    setBusy(r._id);
    try {
      let data;
      if (r.kind === "orden") {
        const cond = String(r.condicion_pago || "").toLowerCase();
        ({ data } = await axios.post(`${API}/treasury/payment-orders`, {
          type: !cond || cond.includes("contado") ? "contado" : "credito",
          project_id: r.project_id,
          items: [{ order_type: r.order_type, order_id: r._id, order_number: r.order_number, invoice_number: r.invoice_number,
                    amount: r.amount, iva_amount: r.iva_amount, provider_name: r.provider_name, project_name: r.project_name }],
        }, { withCredentials: true }));
      } else {
        ({ data } = await axios.put(`${API}/treasury/payment-orders/${r._id}/generar`, {}, { withCredentials: true }));
      }
      toast.success(`Orden de Pago ${data.number} generada — ya está en Órdenes de Pago`);
      await cargar();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar la Orden de Pago"); }
    finally { setBusy(null); }
  };

  // Fix EF (2026-09-24): una solicitud de contrato con terceros sin certificación en la
  // obra (quedó duplicada) no se paga: se descarta.
  const descartar = async (r) => {
    if (!window.confirm(`¿Descartar la solicitud ${r.order_number}? No tiene certificación en la obra.`)) return;
    setBusy(r._id);
    try {
      await axios.put(`${API}/treasury/payment-orders/${r._id}/descartar`, {}, { withCredentials: true });
      toast.success("Solicitud descartada");
      await cargar();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo descartar"); }
    finally { setBusy(null); }
  };

  const visibles = rows.filter((r) => origen === "todos" || r.origen === origen);
  const etiqueta = (k) => (ORIGENES.find((o) => o.key === k) || { label: "Otros" }).label;
  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="solicitudes-pago">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 border-b border-gray-100">
        <p className="text-sm font-bold text-gray-800">Solicitudes de pago <span className="font-normal text-gray-400">({rows.length})</span></p>
        <div className="flex gap-1.5 flex-wrap">
          {ORIGENES.map((o) => (
            <button key={o.key} onClick={() => setOrigen(o.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-xl border ${origen === o.key ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-600 border-gray-200"}`}
              data-testid={`solicitudes-origen-${o.key}`}>
              {o.label}{o.key !== "todos" ? ` (${rows.filter((r) => r.origen === o.key).length})` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50/50 text-xs text-gray-400 uppercase">
            <th className="text-left px-4 py-2.5">Origen</th><th className="text-left px-4 py-2.5">Código</th>
            <th className="text-left px-4 py-2.5">Proveedor / beneficiario</th><th className="text-left px-4 py-2.5">Proyecto</th>
            <th className="text-right px-4 py-2.5">Monto</th><th className="text-left px-4 py-2.5">Fecha</th><th className="px-4 py-2.5"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8"><Loader2 className="animate-spin mx-auto text-gray-400" size={18} /></td></tr>
              : visibles.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">No hay solicitudes pendientes</td></tr>
              : visibles.map((r) => (
                <tr key={`${r.kind}-${r._id}`} className="border-t border-gray-50" data-testid={`solicitud-${r._id}`}>
                  <td className="px-4 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700">{etiqueta(r.origen)}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.order_number}</td>
                  <td className="px-4 py-2.5">{r.provider_name || "—"}{r.concepto ? <span className="block text-xs text-gray-400">{r.concepto}</span> : null}
                    {r.huerfana && <span className="block text-[10px] font-bold text-red-600" data-testid={`solicitud-huerfana-${r._id}`}>Sin certificación en la obra (duplicada): no se paga</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{r.project_name || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-bold">{formatPYG(r.amount)}</td>
                  <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{formatDate(r.invoice_linked_at || r.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.huerfana ? (
                      <button onClick={() => descartar(r)} disabled={busy === r._id}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                        data-testid={`solicitud-descartar-${r._id}`}>
                        {busy === r._id ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Descartar
                      </button>
                    ) : (
                    <button onClick={() => generar(r)} disabled={busy === r._id}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`solicitud-generar-${r._id}`}>
                      {busy === r._id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Generar OP
                    </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Pago Directo Tab ──────────────────────────────────────────────────────────
function PagoDirectoTab({ directPayments, projects, onRefresh, onView, canPay = false, onPay }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    beneficiary: "", concept: "", amount: "", project_id: "",
    reference: "", payment_type: "contado", iva_included: true, notes: "",
  });
  const [saving, setSaving] = useState(false);
  // Fix EE (2026-09-24): el beneficiario se puede elegir del registro de tercerizados.
  const [registro, setRegistro] = useState([]);
  useEffect(() => {
    if (!showModal) return;
    axios.get(`${API}/tercerizados/registro`, { withCredentials: true })
      .then(({ data }) => setRegistro(data?.tercerizados || []))
      .catch(() => setRegistro([]));
  }, [showModal]);

  const handleCreate = async () => {
    if (!form.beneficiary || !form.concept || !form.amount) {
      toast.error("Complete: Beneficiario, Concepto y Monto");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/treasury/direct-payments`, {
        ...form, amount: parseFloat(form.amount),
      }, { withCredentials: true });
      toast.success(`Pago Directo ${res.data.number} generado`);
      setShowModal(false);
      setForm({ beneficiary: "", concept: "", amount: "", project_id: "", reference: "", payment_type: "contado", iva_included: true, notes: "" });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear pago directo");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <SolicitudesPago onRefresh={onRefresh} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{directPayments.length} pagos directos</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl text-xs font-semibold transition-all shadow-sm"
          data-testid="new-direct-payment-btn">
          <Plus size={14} /> Nuevo Pago Directo
        </button>
      </div>

      <div className="bg-indigo-50/60 rounded-xl border border-indigo-100 p-4 text-xs text-indigo-800">
        <p className="font-semibold mb-1">Pago Directo (OP-D)</p>
        <p>Pagos a beneficiarios sin OC/OTS asociada. Pasan por el mismo flujo de aprobación. Ejemplos: honorarios, reintegros, servicios eventuales, alquileres.</p>
      </div>

      {directPayments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <UserCheck size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay pagos directos</p>
          <p className="text-xs text-gray-400 mt-1">Haga clic en «Nuevo Pago Directo» para registrar uno</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">N° OP</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Beneficiario</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Concepto</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Proyecto</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Monto</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {directPayments.map(op => {
                  const st = estadoDocumentalPago(op) || STATUS_MAP[op.status] || { label: op.status, cls: "bg-gray-100 text-gray-600" };
                  return (
                    <tr key={op._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono font-bold text-indigo-700">{op.number}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{op.beneficiary}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate">{op.concept}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{op.project_name || "—"}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">{formatPYG(op.total)}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>{st.label}</span>
                        {op.conciliacion_pago?.estado !== "conciliado" && op.conciliacion_pago?.cantidad > 0 && <p className="text-[10px] text-amber-800 mt-1" data-testid={`dp-conciliacion-${op._id}`}>{op.conciliacion_pago.alertas.length} alerta(s) · revisar comprobantes</p>}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => onView(op)} className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all" data-testid={`view-dp-${op._id}`}>
                          <Eye size={12} /> VER
                        </button>
                        {op.status === "pagada" && canPay && <button onClick={() => onPay(op)}
                          className="mt-1 text-xs px-2.5 py-1 border border-blue-300 text-blue-800 rounded-lg" data-testid={`completar-dp-${op._id}`}>Completar comprobantes</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Nuevo Pago Directo</h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Beneficiario / Proveedor *</label>
                  {registro.length > 0 && (
                    <SelectBuscable value={(registro.find(t => t.razon_social === form.beneficiary) || {}).id || ""}
                      onChange={v => { const t = registro.find(x => x.id === v); setForm(p => ({ ...p, beneficiary: t ? t.razon_social : "" })); }}
                      options={registro.map(t => ({ value: t.id, label: `${t.razon_social}${t.ruc ? ` · RUC ${t.ruc}` : ""}` }))}
                      vacio="Elegir un tercerizado registrado…" placeholderBuscar="Buscar tercerizado..."
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white mb-2" testId="dp-tercerizado-select" />
                  )}
                  <input value={form.beneficiary} onChange={e => setForm(p => ({ ...p, beneficiary: e.target.value }))} placeholder="Nombre o razón social..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-all" data-testid="dp-beneficiary-input" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Concepto *</label>
                  <input value={form.concept} onChange={e => setForm(p => ({ ...p, concept: e.target.value }))} placeholder="Descripción del pago..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-all" data-testid="dp-concept-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto (PYG) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-all" data-testid="dp-amount-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de Pago</label>
                  <select value={form.payment_type} onChange={e => setForm(p => ({ ...p, payment_type: e.target.value }))} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-200" data-testid="dp-type-select">
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proyecto</label>
                  {/* Fix HG (2026-10-20): obras de la A a la Z y con buscador (se puede escribir el nombre). */}
                  <SelectBuscable value={form.project_id} onChange={v => setForm(p => ({ ...p, project_id: v }))}
                    options={opcionesObras(projects)} vacio="Sin proyecto..." placeholderBuscar="Buscar obra..."
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-indigo-200" testId="dp-project-select" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">N° Comprobante</label>
                  <input value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} placeholder="N° factura o recibo..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-all" data-testid="dp-reference-input" />
                </div>
                <div className="col-span-2 flex items-center gap-3">
                  <input type="checkbox" id="dp-iva" checked={form.iva_included} onChange={e => setForm(p => ({ ...p, iva_included: e.target.checked }))} className="w-4 h-4 accent-indigo-600" data-testid="dp-iva-check" />
                  <label htmlFor="dp-iva" className="text-xs text-gray-600 cursor-pointer">Incluye IVA (10%) — se calcula automáticamente</label>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
                  <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Información adicional..." className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
                <button onClick={handleCreate} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-all" data-testid="create-direct-payment-btn">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <UserCheck size={15} />}
                  {saving ? "Generando..." : "Generar Pago Directo"}
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}


// ── CUENTAS POR PAGAR TAB (BLOQUE D) ──────────────────────────────────────────

const SEMAFORO_STYLE = {
  vencido: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", badge: "bg-red-600 text-white", label: "Vencida" },
  rojo: { bg: "bg-red-50", text: "text-red-600", border: "border-red-100", badge: "bg-red-500 text-white", label: "<15 días" },
  amarillo: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-100", badge: "bg-amber-400 text-white", label: "15–30 días" },
  verde: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-100", badge: "bg-emerald-400 text-white", label: ">30 días" },
  gray: { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-100", badge: "bg-gray-400 text-white", label: "S/F" },
};

// Fix CG (2026-09-10): estado de cuenta de un proveedor por período (facturado, pagado y saldo) y su Excel.
// Fix FO (2026-10-09): factura por factura, con vencimiento y atraso, PDF y vista previa (componente
// compartido con el estado de cuenta de clientes).
function ReporteProveedorModal({ proveedor, opciones, onClose }) {
  return <EstadoCuentaModal tipo="proveedor" nombre={proveedor} opciones={opciones} onClose={onClose} testId="reporte" />;
}

function CuentasPorPagarTab() {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState("");
  const [filterSemaforo, setFilterSemaforo] = useState("");
  const [filterCondicion, setFilterCondicion] = useState("");
  const [search, setSearch] = useState("");
  const [showResumen, setShowResumen] = useState(true);
  const [reporteProv, setReporteProv] = useState(null); // Fix CG (2026-09-10)

  // BLOQUE 33 PARTE B — solo el primer fetch muestra el Skeleton; refrescos
  // posteriores (filtros, botón "Actualizar") mantienen la tabla actual visible
  // para no resetear el scroll.
  const hasLoadedOnceRef = useRef(false);
  const fetchData = useCallback(async () => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTipo) params.append("order_type", filterTipo);
      if (filterSemaforo) params.append("semaforo", filterSemaforo);
      if (filterCondicion) params.append("condicion", filterCondicion);

      const [cxpRes, provRes] = await Promise.all([
        axios.get(`${API}/treasury/cuentas-por-pagar?${params}`, { withCredentials: true }),
        axios.get(`${API}/treasury/cuentas-por-pagar/resumen-proveedores`, { withCredentials: true }),
      ]);
      setItems(cxpRes.data || []);
      setResumen(provRes.data || []);
    } catch { toast.error("Error al cargar Cuentas por Pagar"); }
    finally { setLoading(false); hasLoadedOnceRef.current = true; }
  }, [filterTipo, filterSemaforo, filterCondicion]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = items.filter(it =>
    !search ||
    it.proveedor?.toLowerCase().includes(search.toLowerCase()) ||
    it.codigo?.toLowerCase().includes(search.toLowerCase()) ||
    it.nro_factura?.toLowerCase().includes(search.toLowerCase()) ||
    it.proyecto?.toLowerCase().includes(search.toLowerCase())
  );

  const totalDeuda = filtered.reduce((s, i) => s + i.monto, 0);
  const vencidas = filtered.filter(i => i.semaforo === "vencido").length;
  const porVencer = filtered.filter(i => i.semaforo === "rojo").length;

  return (
    <div className="space-y-5" data-testid="cxp-tab">
      {/* KPI bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total CxP</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatPYGCompact(totalDeuda)}</p>
          <p className="text-xs text-gray-400">{filtered.length} facturas pendientes</p>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Vencidas</p>
          <p className="text-xl font-bold text-red-700 mt-1">{vencidas}</p>
          <p className="text-xs text-red-400">Requieren pago urgente</p>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm">
          <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Por Vencer &lt;15d</p>
          <p className="text-xl font-bold text-amber-700 mt-1">{porVencer}</p>
          <p className="text-xs text-amber-400">Atención próxima</p>
        </div>
      </div>

      {/* Resumen por Proveedor */}
      {resumen.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <button onClick={() => setShowResumen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
              <Building2 size={15} className="text-[#2E6DA4]" /> Saldo por Proveedor
            </div>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${showResumen ? "rotate-180" : ""}`} />
          </button>
          {showResumen && (
            <div className="border-t border-gray-100 overflow-x-auto" data-testid="cxp-resumen-proveedores">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50/50 text-gray-400 uppercase">
                  <th className="text-left px-4 py-2">Proveedor</th><th className="text-right px-4 py-2">Facturas</th>
                  <th className="text-right px-4 py-2">Contado</th><th className="text-right px-4 py-2">Crédito</th>
                  <th className="text-right px-4 py-2">Vencido</th><th className="text-right px-4 py-2">Saldo</th><th className="px-4 py-2"></th>
                </tr></thead>
                <tbody>
                  {resumen.map((prov) => (
                    <tr key={prov.proveedor} className="border-t border-gray-50" data-testid="cxp-prov-row">
                      <td className="px-4 py-2 font-semibold text-gray-800">{prov.proveedor}</td>
                      <td className="px-4 py-2 text-right">{prov.count}</td>
                      <td className="px-4 py-2 text-right">{formatPYG(prov.contado)}</td>
                      <td className="px-4 py-2 text-right">{formatPYG(prov.credito)}</td>
                      <td className={`px-4 py-2 text-right ${prov.vencido > 0 ? "text-red-600 font-semibold" : "text-gray-400"}`}>{formatPYG(prov.vencido)}</td>
                      <td className="px-4 py-2 text-right font-bold text-gray-900">{formatPYG(prov.total)}</td>
                      <td className="px-4 py-2 text-right"><button onClick={() => setReporteProv(prov.proveedor)} className="text-[#2E6DA4] font-semibold hover:underline" data-testid="cxp-reporte-btn">Reporte</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {reporteProv !== null && <ReporteProveedorModal proveedor={reporteProv} opciones={resumen.map(p => p.proveedor)} onClose={() => setReporteProv(null)} />}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] transition-all"
              placeholder="Proveedor, código, factura..."
              value={search} onChange={e => setSearch(e.target.value)}
              data-testid="cxp-search" />
          </div>
          {/* Fix FO (2026-10-09): cuenta corriente de cualquier proveedor, también los que ya no deben */}
          <button onClick={() => setReporteProv("")}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-[#2E6DA4]/40 text-[#2E6DA4] hover:bg-blue-50"
            data-testid="cxp-estado-cuenta-btn"><FileText size={13} /> Estado de cuenta</button>

          <div className="flex gap-1.5">
            {[{ v: "", l: "Todos" }, { v: "OC", l: "OC" }, { v: "OTS", l: "OTS" }, { v: "RRHH", l: "RRHH" }].map(o => (
              <button key={o.v} onClick={() => setFilterTipo(o.v)}
                className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-all ${filterTipo === o.v ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
                data-testid={`cxp-filter-tipo-${o.v || "todos"}`}>
                {o.l}
              </button>
            ))}
          </div>

          <div className="flex gap-1.5">
            {[
              { v: "", l: "Todos", dot: "bg-gray-400" },
              { v: "vencido", l: "Vencidas", dot: "bg-red-600" },
              { v: "rojo", l: "<15d", dot: "bg-red-400" },
              { v: "amarillo", l: "15-30d", dot: "bg-amber-400" },
              { v: "verde", l: ">30d", dot: "bg-emerald-500" },
            ].map(o => (
              <button key={o.v} onClick={() => setFilterSemaforo(o.v)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border transition-all ${filterSemaforo === o.v ? "bg-slate-800 text-white border-slate-800" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}
                data-testid={`cxp-filter-sem-${o.v || "todos"}`}>
                <span className={`w-2 h-2 rounded-full ${o.dot}`} />{o.l}
              </button>
            ))}
          </div>

          <button onClick={fetchData} className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 transition-colors" title="Actualizar">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Facturas Pendientes de Pago</p>
          <span className="text-xs text-gray-400">{filtered.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["", "Tipo", "Código", "Proveedor", "Proyecto", "N° Factura", "Monto", "Condición", "Vencimiento", "Estado"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 10 }).map((__, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400 text-sm">
                  <CheckCircle2 size={32} className="mx-auto mb-2 opacity-20" />
                  <p>No hay cuentas por pagar pendientes</p>
                </td></tr>
              ) : filtered.map(item => {
                const sem = SEMAFORO_STYLE[item.semaforo] || SEMAFORO_STYLE.gray;
                return (
                  <tr key={item._id} className={`border-b border-gray-50 transition-colors hover:opacity-90 ${sem.bg}`}>
                    <td className="px-3 py-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.semaforo === "vencido" || item.semaforo === "rojo" ? "bg-red-500 animate-pulse" : item.semaforo === "amarillo" ? "bg-amber-400" : "bg-emerald-400"}`} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.tipo === "OC" ? "bg-blue-100 text-blue-700" : item.tipo === "OTS" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                        {item.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono font-bold text-gray-700">{item.codigo || "—"}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{item.proveedor || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{item.proyecto || "—"}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600" title={(item.nro_facturas || []).join(", ")}>
                      {invoiceNumbersLabel(item.nro_facturas?.length ? item.nro_facturas : (item.nro_factura ? [item.nro_factura] : []))}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">{formatPYGCompact(item.monto)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {item.condicion === "contado" ? "Contado" : item.condicion || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {item.dias_vencimiento !== null && item.dias_vencimiento !== undefined ? (
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${sem.badge}`}>
                          {item.dias_vencimiento < 0 ? `${Math.abs(item.dias_vencimiento)}d atrás` : item.dias_vencimiento === 0 ? "Hoy" : `${item.dias_vencimiento}d`}
                        </span>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold ${sem.text}`}>{sem.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/30 flex justify-between">
            <span className="text-xs text-gray-500">{filtered.length} registros</span>
            <span className="text-sm font-bold text-gray-900">Total: {formatPYGCompact(totalDeuda)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TesoreriaPagos() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const openNumber = searchParams.get("open") || "";
  const [activeTab, setActiveTab] = useState(openNumber ? "ordenes" : "contado");
  const [eligibleContado, setEligibleContado] = useState([]);
  const [eligibleCredito, setEligibleCredito] = useState([]);
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(true);
  const [loadingOPs, setLoadingOPs] = useState(true);
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [viewOP, setViewOP] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null); // Fix CD (2026-09-10)
  const [approveTarget, setApproveTarget] = useState(null); // Fix FR (2026-10-12)

  // Fix CD (2026-09-10): se suma Gerencia Financiera y se comparan los roles
  // normalizados — con `includes` literal, "Gerencia Técnica" (el nombre canónico)
  // no veía los botones aunque el backend sí la dejaba aprobar.
  const canApprove = roleAllowed(user?.role, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Financiera"]);
  const canPay = roleAllowed(user?.role, ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Financiera", "Contador"]);

  // BLOQUE 33 PARTE B — el Skeleton solo debe verse en la carga inicial de cada
  // lista; en refrescos posteriores (ej. al cerrar el modal de detalle de una OP
  // tras aprobar/rechazar/generar) mantenemos la tabla actual mientras llega la
  // data nueva, evitando el remount que devolvía el scroll al tope del listado.
  const eligibleLoadedRef = useRef(false);
  const opsLoadedRef = useRef(false);

  const loadEligible = useCallback(async () => {
    if (!eligibleLoadedRef.current) setLoadingEligible(true);
    try {
      // Cargamos en paralelo por condición de pago — el backend filtra por
      // condicion_pago == "contado" (case-insensitive) vs cualquier otro string.
      const [contadoRes, creditoRes] = await Promise.all([
        axios.get(`${API}/treasury/eligible-orders`, { params: { payment_type: "contado" }, withCredentials: true }),
        axios.get(`${API}/treasury/eligible-orders`, { params: { payment_type: "credito" }, withCredentials: true }),
      ]);
      setEligibleContado(contadoRes.data);
      setEligibleCredito(creditoRes.data);
    } catch {
      toast.error("Error al cargar órdenes disponibles");
    } finally {
      setLoadingEligible(false);
      eligibleLoadedRef.current = true;
    }
  }, []);

  const loadPaymentOrders = useCallback(async () => {
    if (!opsLoadedRef.current) setLoadingOPs(true);
    try {
      const [opRes, statsRes, projRes] = await Promise.all([
        axios.get(`${API}/treasury/payment-orders`, { withCredentials: true }),
        axios.get(`${API}/treasury/stats`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
      ]);
      setPaymentOrders(opRes.data);
      setStats(statsRes.data);
      setProjects(projRes.data);
    } catch {
      toast.error("Error al cargar órdenes de pago");
    } finally {
      setLoadingOPs(false);
      opsLoadedRef.current = true;
    }
  }, []);

  const handleRefresh = useCallback(() => {
    loadEligible();
    loadPaymentOrders();
  }, [loadEligible, loadPaymentOrders]);

  useEffect(() => {
    loadEligible();
    loadPaymentOrders();
  }, []);

  // BLOQUE 34 — deep-link desde el Dashboard de "Jefe de Compras": abre
  // directamente el modal de la OP clickeada (?open=<numero OP>).
  const openedOPRef = useRef(false);
  useEffect(() => {
    if (!openNumber || openedOPRef.current || !paymentOrders.length) return;
    const target = paymentOrders.find(op => op.number === openNumber);
    if (target) {
      setViewOP(target);
      openedOPRef.current = true;
    }
  }, [openNumber, paymentOrders]);

  const handleApprove = async (monto) => {
    if (!approveTarget) return;
    const op = approveTarget;
    try {
      const { data } = await axios.put(`${API}/treasury/payment-orders/${op._id}/approve`, monto ? { monto_aprobado: monto } : {}, { withCredentials: true });
      if (data?.aprobacion_pendiente) {   // Fix GT: falta la aprobación de otros perfiles
        toast.success(`Tu aprobación (${data.aprobacion_pendiente.perfil}) quedó registrada — falta ${data.aprobacion_pendiente.faltan.join(", ")}`);
      } else {
        toast.success(monto ? `Orden ${op.number} aprobada por ${formatPYG(monto)} — el saldo quedó pendiente en una OP nueva` : `Orden ${op.number} aprobada`);
      }
      setApproveTarget(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aprobar");
    }
  };

  const handleReject = async (reason, darDeBaja = false) => {
    if (!rejectTarget) return;
    try {
      await axios.put(`${API}/treasury/payment-orders/${rejectTarget._id}/reject`, { reason, dar_de_baja_oc: darDeBaja }, { withCredentials: true });   // Fix IL
      toast.success(darDeBaja ? `Orden ${rejectTarget.number} rechazada y OC dada de baja` : `Orden ${rejectTarget.number} rechazada`);
      setRejectTarget(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al rechazar");
    }
  };

  // Fix IL (2026-10-30): si una OC se dio de baja por error, vuelve a quedar para pagar.
  const handleRehabilitarOC = async (op) => {
    try {
      await axios.put(`${API}/treasury/payment-orders/${op._id}/rehabilitar-oc`, {}, { withCredentials: true });
      toast.success(`La OC de ${op.number} vuelve a quedar para pagar`);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo rehabilitar la OC");
    }
  };

  // Fix CD (2026-09-10): un solo PUT hace todo (aprobar si hace falta, adjuntar el
  // comprobante y cerrar). Si falla, la ventana queda abierta con el mensaje.
  const handlePagar = async (payload) => {
    if (!payTarget) return;
    try {
      const completar = payTarget.status === "pagada";
      const { data } = await axios.put(`${API}/treasury/payment-orders/${payTarget._id}/${completar ? "completar-comprobantes" : "registrar-pago"}`, payload, { withCredentials: true });
      toast.success(completar ? "Comprobantes completados; se conservó el movimiento existente" : `Pago registrado · ${estadoDocumentalPago(data)?.label || "revisar comprobantes"}`);
      setPayTarget(null);
      handleRefresh();
    } catch (err) {
      if (err.response) throw err;   // Fix GR: las alertas del comprobante las muestra la ventana
      toast.error(err.response?.data?.detail || "Error al registrar el pago");
    }
  };

  const pendingCount = paymentOrders.filter((op) => op.status === "pendiente_aprobacion").length;
  const directPayments = paymentOrders.filter((op) => op.type === "directo" && !esSolicitud(op));
  const ordenes = paymentOrders.filter((op) => !esSolicitud(op)); // Fix CF (2026-09-10)

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Tesorería</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Pagos y Órdenes de Pago</h1>
        <p className="text-sm text-gray-500 mt-0.5">Generación y aprobación de Órdenes de Pago desde OCs y OTSs facturadas</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="OPs Pendientes" value={stats.pendientes ?? 0} sub={`${formatPYGCompact(stats.monto_pendiente || 0)} PYG`} color="amber" icon={Clock} />
        <StatCard title="OPs Aprobadas" value={stats.aprobadas ?? 0} sub={`${formatPYGCompact(stats.monto_aprobado || 0)} PYG`} color="emerald" icon={CheckCircle2} />
        {/* Fix CD (2026-09-10) */}
        <StatCard title="OPs Pagadas" value={stats.pagadas ?? 0} sub={`${formatPYGCompact(stats.monto_pagado || 0)} PYG`} color="blue" icon={DollarSign} />
        <StatCard title="OPs Rechazadas" value={stats.rechazadas ?? 0} color="red" icon={XCircle} />
        <StatCard title="Disponibles" value={eligibleContado.length + eligibleCredito.length} sub="Listas para pago" color="blue" icon={Banknote} />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0.5 overflow-x-auto pt-1">{/* Fix GT: con la pestaña nueva, en una sola línea (se desplaza si no entra) */}
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const badgeCount = tab.key === "ordenes" && pendingCount > 0 ? pendingCount : null;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all relative whitespace-nowrap flex-shrink-0 ${isActive ? "border-slate-800 text-slate-800" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
                data-testid={`tab-${tab.key}`}
              >
                <Icon size={14} />
                {tab.label}
                {badgeCount && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "tareas" && (
        <TareasPanel modulo="tesoreria" titulo="Tareas y Flujo de Trabajo — Tesorería" />
      )}
      {activeTab === "contado" && (
        <ContadoTab eligible={eligibleContado.filter((o) => o.order_type === "OC")} loadingEligible={loadingEligible} projects={projects} onRefresh={handleRefresh} />
      )}
      {activeTab === "credito" && (
        <CreditoTab eligible={eligibleCredito.filter((o) => o.order_type === "OC")} loadingEligible={loadingEligible} projects={projects} onRefresh={handleRefresh} />
      )}
      {activeTab === "pagodirecto" && (
        <PagoDirectoTab directPayments={directPayments} projects={projects} onRefresh={handleRefresh} onView={setViewOP} canPay={canPay} onPay={setPayTarget} />
      )}
      {activeTab === "ordenes" && (
        <OrdenesTab
          paymentOrders={ordenes}
          loadingOPs={loadingOPs}
          onApprove={setApproveTarget}
          onRejectClick={setRejectTarget}
          onView={setViewOP}
          canApprove={canApprove}
          canPay={canPay}
          onPay={setPayTarget}
          onRehabilitarOC={handleRehabilitarOC}
        />
      )}
      {activeTab === "cxp" && <CuentasPorPagarTab />}
      {activeTab === "perfiles" && <PerfilesAprobacionPagos />}{/* Fix GT */}

      {/* Modals */}
      {viewOP && <OPDetailModal op={viewOP} onClose={() => setViewOP(null)} onRefresh={handleRefresh} />}
      {approveTarget && <AprobarOPModal op={approveTarget} onConfirm={handleApprove} onClose={() => setApproveTarget(null)} />}
      {rejectTarget && <RejectModal op={rejectTarget} onConfirm={handleReject} onClose={() => setRejectTarget(null)} />}
      {payTarget && <RegistrarPagoModal op={payTarget} onConfirm={handlePagar} onClose={() => setPayTarget(null)} />}
    </div>
  );
}
